import './style.css';
import {
  CAPSULE_FIELD,
  LIMITS,
  daysBetween,
  localDateOf,
  parseLocalDate,
  type CapsuleDto,
  type Change,
  type LocalDate,
  type MePatch,
  type MeResponse,
  type MemberDto,
  type MemoryConflictBody,
  type MemoryDto,
  type NoteDto,
  type PerspectiveConflictBody,
  type PerspectiveDto,
  type PhotoDto,
  type SnapshotResponse,
  type SpaceDto,
  type SpacePatch,
  type ThemeDto,
  type VoiceDto,
  type VoicesResponse,
  type VoiceConflictBody,
} from '@star-sea/shared';
import { thumbHashToDataURL } from 'thumbhash';
import { Api, ApiError } from './api.ts';
import { FIELD_LABELS, FIELD_NAMES, changesOf, editNote, fieldsOf, isEmpty, rebase, toPatch, type FieldChanges, type FieldName, type MemoryFields } from './edit.ts';
import { fakeSnapshot } from './fake.ts';
import { exportDialog } from './exportui.ts';
import { cardFaces } from './flip.ts';
import { mountLocalGate } from './gateui.ts';
import { voiceDialog, VoiceConflict } from './voiceui.ts';
import { Galaxy } from './galaxy.ts';
import { glimpseRoute } from './glimpse.ts';
import { countdown, dotted, midnightOf, phaseOf, unlockPresets, type CapsulePhase } from './capsule.ts';
import { recoveredNotice } from './devices.ts';
import { devicesDialog } from './devicesui.ts';
import { canVibrate, chargeBuzz, haptics, heartbeatBuzz, shatterBuzz, sinkBuzz, stopBuzz, tickBuzz } from './haptics.ts';
import { playAnniversary, playGlimpse, playIntro } from './intro.ts';
import { footprintsOf, lastReadNote, myPendingNote, noteByline, noteHold, pendingNote } from './notes.ts';
import { clearOffline, forgetThumbs, loadSnapshot, offlineStats, saveSnapshot } from './offline.ts';
import { inviteDefaults, latestPhoto } from './relay.ts';
import { INTRO_MODES, afterIntro, occasionText, parseMemo, pickIntro, type Birthday, type IntroMemo, type IntroMode } from './ritual.ts';
import { pickStops, stopHold, type RoamDay } from './roam.ts';
import { mountRoamUi, type RoamSlide } from './roamui.ts';
import { createSound } from './sound.ts';
import { THEME_SUGGESTIONS, stationIndex, stationsOf, stepStation } from './themes.ts';
import { Stardust } from './stardust.ts';
import type { CardImage } from './carousel.ts';
import {
  capsuleDialog,
  confirmDialog,
  errorScreen,
  footprintDialog,
  inviteDialog,
  loadingScreen,
  memoryEditor,
  mountBanner,
  mountCapsulePanel,
  mountCaption,
  mountConstellationBar,
  mountHud,
  mountIntro,
  mountIntroWords,
  mountPanel,
  mountShell,
  noteDialog,
  pairScreen,
  relayScreen,
  SideConflict,
  setupScreen,
  spaceDialog,
  swatchCanvas,
  themeDialog,
  toast,
  trashDialog,
  type CapsuleView,
  type InviteDraft,
  type MemoryEditor,
  type PairValues,
  type PanelContent,
  type ToastHandle,
} from './ui.ts';
import { deviceLabel, isHeic, uploadForm } from './upload.ts';
import { defaultWords } from './words.ts';

const api = new Api();
const appRoot = document.getElementById('app')!;
const shell = mountShell(appRoot);
const sound = createSound();
let onGateLock = () => {};
let releaseGateSound: (() => void) | null = null;
const localGate = mountLocalGate({
  root: appRoot,
  onLock: () => { releaseGateSound ??= sound.hold('recording'); onGateLock(); },
  onUnlock: () => { releaseGateSound?.(); releaseGateSound = null; },
  onReset: async () => { await forget(); localGate.reset(); },
});
// 另一标签页退出或更换账号，不能沿用内存中的旧设备身份。
addEventListener('storage', (e) => { if (e.key === 'star-sea/token' || e.key === null) location.reload(); });
const params = new URLSearchParams(location.search);
const fakeCount = Math.min(200_000, Math.max(0, Math.floor(Number(params.get('fake') ?? 0)) || 0));
const showStats = import.meta.env.DEV || fakeCount > 0 || params.has('stats');
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
// 手机和小屏：照片切换的形变粒子少一些（同原型）
const lowEnd = matchMedia('(pointer: coarse)').matches || Math.min(innerWidth, innerHeight) < 560;
// 星尘：照片面板打开、关上，入场那段话飘走，还有接力仪式里照片凝聚、收进光核都用它。
// WebGL 第一次用到才建，所以页面一开就建好放着——进星海之前的接力仪式用的也是这一个。
// 粒子化的格点：桌面 128×128、手机 96×96（同原型）
const dust = new Stardust(shell.fx, { grid: lowEnd ? 96 : 128 });
// ?intro=0 跳过入场动画（压测、调试时用）；?intro=full|anniversary|daily 指定放哪一档（ritual.ts）
const introParam = params.get('intro');
const introOn = introParam !== '0';
/** 这台设备看过哪一档入场（ritual.ts） */
const INTRO_KEY = 'star-sea/intro';
/** 星海设置里点了「重看完整入场」：记一下再刷新，刷新以后放完整的那档（只用一次） */
const INTRO_NEXT_KEY = 'star-sea/intro-next';
/** 这台设备上次见到的成员：Ta 不在的时候加入了，下次打开时放一次「赴约」的回响 */
const MEMBERS_KEY = 'star-sea/members-seen';

function readIntroMemo(): IntroMemo | null {
  try {
    return parseMemo(localStorage.getItem(INTRO_KEY));
  } catch {
    return null;
  }
}

function saveIntroMemo(memo: IntroMemo): void {
  try {
    localStorage.setItem(INTRO_KEY, JSON.stringify(memo));
  } catch {
    // 浏览器不让存：下次还当第一次
  }
}

/** 网址指定的、或者「重看完整入场」留下的那一档；都没有返回 null */
function forcedIntro(): IntroMode | null {
  if (INTRO_MODES.includes(introParam as IntroMode)) return introParam as IntroMode;
  try {
    const next = sessionStorage.getItem(INTRO_NEXT_KEY);
    sessionStorage.removeItem(INTRO_NEXT_KEY);
    return next === 'full' ? 'full' : null;
  } catch {
    return null;
  }
}

/** 上次见到的成员 id；这台设备还没记过返回 null */
function readMembersSeen(): string[] | null {
  try {
    const v = JSON.parse(localStorage.getItem(MEMBERS_KEY) ?? 'null') as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : null;
  } catch {
    return null;
  }
}

function saveMembersSeen(ids: string[]): void {
  try {
    localStorage.setItem(MEMBERS_KEY, JSON.stringify(ids));
  } catch {
    // 浏览器不让存：下次可能再放一次回响
  }
}
// 画质档位（quality.ts）：按设备记住自动调到的那档；?quality=N 固定在第 N 档（调试用，不记住）
const QUALITY_KEY = 'star-sea/quality';
const forcedQuality = params.has('quality') ? Number(params.get('quality')) || 0 : null;

function readQuality(): number {
  try {
    return Number(localStorage.getItem(QUALITY_KEY)) || 0;
  } catch {
    return 0;
  }
}

function saveQuality(level: number): void {
  try {
    localStorage.setItem(QUALITY_KEY, String(level));
  } catch {
    // 浏览器不让存：下次从头调
  }
}

const WEEKDAY = '日一二三四五六';
/** 星轨开关按设备记住（同原型）；默认开 */
const TRAIL_KEY = 'star-sea/trail';

function readTrail(): boolean {
  try {
    return localStorage.getItem(TRAIL_KEY) !== '0';
  } catch {
    return true;
  }
}

function saveTrail(on: boolean): void {
  try {
    localStorage.setItem(TRAIL_KEY, on ? '1' : '0');
  } catch {
    // 浏览器不让存：这次打开时有效
  }
}

function coordinates(lat: number, lng: number): string {
  return `${lat >= 0 ? '北纬' : '南纬'} ${Math.abs(lat).toFixed(3)}° · ${lng >= 0 ? '东经' : '西经'} ${Math.abs(lng).toFixed(3)}°`;
}

function dayText(date: LocalDate): string {
  const { y, m, d } = parseLocalDate(date);
  return `${y} 年 ${m} 月 ${d} 日`;
}

/** 改回忆撞上 Ta 刚改过（409）时，服务端附带的最新版本 */
function conflictOf(e: unknown): MemoryDto | null {
  if (!(e instanceof ApiError) || e.status !== 409 || !e.body || typeof e.body !== 'object' || !('memory' in e.body)) return null;
  return (e.body as MemoryConflictBody).memory;
}

function showScreen(node: HTMLElement | null): void {
  shell.overlay.replaceChildren(...(node ? [node] : []));
}

/** 邀请链接形如 …/#invite=CODE；码放在 # 后面，不会发到服务器、也不会进访问日志 */
function takeInviteFromHash(): string {
  const m = /^#invite=([0-9A-Za-z-]+)$/.exec(location.hash);
  return m ? m[1]! : '';
}

/** 网址里带着邀请链接（不取走，留给 showPair） */
function hasInviteInHash(): boolean {
  return takeInviteFromHash() !== '';
}

function clearHash(): void {
  if (location.hash) history.replaceState(null, '', location.pathname + location.search);
}

/** 已经有星海还打开了邀请链接：进去以后说一声 */
let enterToast: string | null = null;

/** 这台设备刚经邀请链接加入（P3b，design §11.12）：这次打开的末尾放两颗星的并轨。start() 里读一次就清掉；手动输码加入的不放 */
let justJoined = false;

/** 这台设备不能再用了（令牌失效、被移除）：令牌和离线缓存一起清掉 */
async function forget(): Promise<void> {
  api.setToken(null);
  await clearOffline();
}

/** 连不上服务时，用上次同步的快照打开星海（offline.ts）；没有缓存返回 false */
async function startOffline(): Promise<boolean> {
  if (!api.token) return false;
  const cached = await loadSnapshot();
  if (!cached) return false;
  await localGate.wait();
  clearHash();
  start(cached.snapshot, cached.me, { offlineSince: cached.savedAt });
  return true;
}

async function boot(): Promise<void> {
  await localGate.wait();
  if (fakeCount > 0) return start(fakeSnapshot(fakeCount), null);
  showScreen(loadingScreen());
  let setup: boolean;
  try {
    setup = (await api.health()).setup;
  } catch {
    // 有上次同步的星海就先显示它，连上以后自动跟上
    if (await startOffline()) return;
    // 设备明确没联网时直接说；联着网也可能上不了外网，所以另一种情况不下结论，原因都列上
    showScreen(
      navigator.onLine === false
        ? errorScreen('这台设备没有联网', '连上 Wi-Fi 或打开移动数据，再点「重试」。', () => void boot())
        : errorScreen('连不上星海服务', '星海服务没有回应：可能是网络不太好，也可能是服务正在更新、或者暂时停了。等一会儿再重试；一直连不上的话，请维护星海的那个人看看服务开着没有。', () => void boot()),
    );
    return;
  }
  // 带令牌时 setup 说的是令牌所在的那片：它没有了（比如重新装过），旧的令牌和离线缓存都不再有用。
  // 不带令牌时说的只是默认那片（ADR-0016，一个服务里有好几片星海），不能凭它清什么
  if (!setup && api.token) await forget();
  if (!api.token) {
    // 默认那片还没建（刚装好的服务）、又不是拿着邀请链接来的：先给建星海页，那里也能回到加入页
    if (!setup && !hasInviteInHash()) return showSetup();
    return showPair();
  }
  enterToast = takeInviteFromHash() ? '这台设备已经在星海里了' : null;
  await enter();
}

/** 配对：成功就进星海。加入页和接力仪式共用 */
async function submitPair(code: string, displayName?: string): Promise<void> {
  const s = await api.pair({ code, displayName: displayName || undefined, deviceLabel: deviceLabel() });
  api.setToken(s.token);
  clearHash();
  await enter();
}

/**
 * Ta 打开邀请链接（#invite=…）：先拆开看发起的人附的那颗星和那句话，长按 2 秒
 * 注入光核，再填称呼进星海（P3a，design §11.12；ADR-0013）。加设备的码不走仪式。
 */
function showRelay(code: string): void {
  // 蓄力的声音和震动（同拆胶囊）：松开、按满时一起停
  let stopCharge: (() => void) | null = null;
  let chargeTick = 0;
  const stopCharging = () => {
    stopCharge?.();
    stopCharge = null;
    clearInterval(chargeTick);
    chargeTick = 0;
    stopBuzz();
  };
  showScreen(
    relayScreen(
      { dust, reduced: reducedMotion },
      {
        load: () => api.previewInvite(code),
        onPress: (k) => {
          if (k < 0) return stopCharging();
          if (stopCharge) return;
          sound.unlock();
          stopCharge = sound.charge(2);
          chargeBuzz();
          chargeTick = window.setInterval(chargeBuzz, 1000);
        },
        onLit: () => {
          stopCharging();
          sound.chime();
          shatterBuzz();
        },
        join: (displayName) => {
          // 从邀请链接加入的这一次才有并轨（P3b）：start() 里读一次就清掉
          justJoined = true;
          return submitPair(code, displayName);
        },
        pair: () => {
          // 仪式中途改去手动输码：不是「从邀请链接加入」，不放并轨
          justJoined = false;
          showScreen(pairScreen(code, (v) => submitPair(v.code, v.displayName || undefined), showSetup));
        },
      },
    ),
  );
}

function showPair(): void {
  const invite = takeInviteFromHash();
  // 带着邀请码先拆预览；加设备的码、找回码、失效后「手动输入」都落到加入页表单
  if (invite) return showRelay(invite);
  showScreen(pairScreen('', (v) => submitPair(v.code, v.displayName || undefined), showSetup));
}

/** 建一片新星海：用维护星海的人给的设置码（ADR-0016）；也能回到加入页 */
function showSetup(): void {
  showScreen(
    setupScreen(async (v) => {
      const s = await api.setup({ ...v, deviceLabel: deviceLabel() });
      api.setToken(s.token);
      await enter();
    }, showPair),
  );
}

async function enter(): Promise<void> {
  try {
    const [me, snap] = await Promise.all([api.me(), api.snapshot()]);
    await localGate.wait();
    clearHash();
    void saveSnapshot(me, snap);
    start(snap, me);
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      await forget();
      return showPair();
    }
    // 健康检查过了、取快照时又断了：同样先显示离线缓存
    if (e instanceof ApiError && (e.status === 0 || e.status >= 500) && (await startOffline())) return;
    showScreen(errorScreen('打开星海时出错了', e instanceof Error ? e.message : String(e), () => void boot()));
  }
}

/** offlineSince：连不上服务，用的是这个时间存下的离线缓存 */
function start(snap: SnapshotResponse, me: MeResponse | null, opts: { offlineSince?: string } = {}): void {
  showScreen(null);
  // 已经有星海还打开了邀请链接：进来以后说一声
  if (enterToast) {
    toast(shell.toasts, enterToast, { ms: 8000 });
    enterToast = null;
  }
  const fake = me === null;
  const members = new Map<string, MemberDto>(snap.members.map((m) => [m.id, m]));
  const memories = new Map<string, MemoryDto>(snap.memories.map((m) => [m.id, m]));
  /** 主题星座（ADR-0011，design §11.10）：主题；每段回忆打了哪些（快照里的回忆带 themeIds，之后的变化看 memory.themes） */
  const themes = new Map<string, ThemeDto>((snap.themes ?? []).map((t) => [t.id, t]));
  const memoryThemes = new Map<string, Set<string>>();
  for (const m of snap.memories) if (m.themeIds?.length) memoryThemes.set(m.id, new Set(m.themeIds));
  /** 正在看的星座（主题 id），null 是没在看；stationDay 是星座里最近看的那一站（换站从它算起） */
  let constellation: string | null = null;
  let stationDay: LocalDate | null = null;
  /** 这一天打上的主题，给面板的主题行 */
  const themesOfDay = (memoryId: string) =>
    [...(memoryThemes.get(memoryId) ?? [])].flatMap((id) => {
      const t = themes.get(id);
      return t ? [{ id, name: t.name, active: id === constellation }] : [];
    });
  /** 照片上的视角（双面翻牌，ADR-0011），按照片分：服务端按你能看到的给，互盲时 Ta 那面可能只有 hidden */
  const voices = new Map<string, VoiceDto[]>();
  for (const v of snap.voices ?? []) voices.set(v.photoId, [...(voices.get(v.photoId) ?? []), v]);
  const voiceVersions = new Map<string, number>();
  let voiceResetSeq = snap.seq;
  let voiceView: { close(): void; photoId: string; memberId: string } | null = null;
  const photoSoundHolds = new Map<string, () => void>();
  const photoFocus = (on: boolean, surface = 'panel') => {
    if (on && !photoSoundHolds.has(surface)) photoSoundHolds.set(surface, sound.hold('photo'));
    else if (!on) { photoSoundHolds.get(surface)?.(); photoSoundHolds.delete(surface); }
  };
  const closeVoice = () => voiceView?.close();
  const perspectives = new Map<string, PerspectiveDto[]>();
  for (const p of snap.perspectives ?? []) perspectives.set(p.photoId, [...(perspectives.get(p.photoId) ?? []), p]);
  /** 星海的名字和在一起的日子，可以改（applySpace）；spaceSeq 是它对应的变更序号 */
  let space = snap.space;
  let spaceSeq = snap.seq;
  const galaxy = new Galaxy(shell.stage, shell.labels, {
    reducedMotion,
    thumbs: { fake, token: api.token },
    quality: forcedQuality !== null ? { start: forcedQuality, fixed: true } : { start: readQuality(), fixed: false, onChange: saveQuality },
  });
  /** 你们俩的生日（ADR-0011，成员自己填）：纪念日入场、胶囊的解封日子、语义缩放的生日射线都用 */
  const birthdays = (): Birthday[] =>
    [...members.values()].flatMap((m) => (m.birthday ? [{ name: m.displayName, monthDay: m.birthday, mine: m.id === me?.member.id }] : []));
  const birthdayLabels = () => birthdays().map((b) => ({ label: b.mine ? '我的生日' : `${b.name} 的生日`, monthDay: b.monthDay }));
  // 入场分三档（ritual.ts）：这台设备第一次打开放完整的，纪念日（含生日）升起极光，平时星从中心的光点里绽开
  const today = localDateOf(new Date());
  const memo = readIntroMemo();
  const plan = introOn ? pickIntro(memo, space.since, today, forcedIntro(), birthdays()) : null;
  // 完整入场时星一开始就在爱心上；另外两档星在自己的位置上、从中心绽开；没有入场时按日期依次点亮
  galaxy.load(snap, !plan);
  galaxy.setBirthdays(birthdayLabels());
  /**
   * 双星核（P3b，design §11.12）：只有一个人是原来的单光晕（off）；两个人都在、没有并轨要放就
   * 绕转（orbit）；有并轨要放先藏着（held），中心还是单光晕，playMerge 放完自然进入绕转。
   * 要放并轨的两次：Ta 从邀请链接加入的这一次（just，读一次就清掉；手动输码加入的不放），和
   * Ta 在这台设备没开着时加入、这次打开补放（catchup：members-seen 里有自己、没有 Ta；
   * 这台设备第一次记不算——可能是刚加入的这一方，或者换了新设备）。
   * mine 是这台设备上的人的 tint——「来过」亮的是对方那颗，没有就当没有对方（压测）。
   */
  const partner = me ? [...members.values()].find((m) => m.id !== me.member.id) : undefined;
  const just = justJoined;
  justJoined = false;
  const seen = me ? readMembersSeen() : null;
  const catchup = !!(me && partner && seen?.includes(me.member.id) && !seen.includes(partner.id));
  galaxy.setTwins(members.size >= 2 ? (just || catchup ? 'held' : 'orbit') : 'off', me?.member.tint);
  // 「来过」：进来这一刻用快照里对方那项的 recent（两个人都开了开关、对方 24 小时内来过才有）；自己关着就不亮
  galaxy.setPartnerRecent(me?.sharePresence === true && partner?.recent === true);
  /**
   * 入场那段话：没写就用默认的「在一起的第 N 天 / 已经点亮了 M 颗星」（这里也是星海设置里的提示文字）。
   * 纪念日前面加一行「在一起两周年」；默认文案的第一行就是「在一起的第 N 天」，整百天那天别说两遍
   */
  const introWords = () => {
    const base = space.introText || defaultWords(daysBetween(space.since, localDateOf(new Date())) + 1, galaxy.count);
    const line = plan?.occasion ? occasionText(plan.occasion) : '';
    return !line || base.startsWith(line) ? base : `${line}\n${base}`;
  };
  /**
   * 入场暗号（ADR-0011，notes.ts）：Ta 留给我、我还没看到的那句，这次入场时代替那段话，下面一行小字「来自 阿禾 · 昨夜留」；
   * 字浮现的那一刻算看到了（之后它是留的那天的足迹）。没有入场动画（?intro=0）时不放，留到下次
   */
  const notes = new Map<string, NoteDto>((snap.notes ?? []).map((n) => [n.id, n]));
  const incoming = me && plan ? pendingNote(notes.values(), me.member.id) : null;
  const noteBy = incoming ? noteByline(incoming, members.get(incoming.from)?.displayName ?? 'Ta', Date.now()) : undefined;
  /** 入场浮现的字：有 Ta 的暗号时换成它，纪念日那一行照样在最前面 */
  const wordsText = () => {
    if (!incoming) return introWords();
    const line = plan?.occasion ? occasionText(plan.occasion) : '';
    return line ? `${line}\n${incoming.text}` : incoming.text;
  };
  let noteSeen = false;
  const seeNote = () => {
    if (!incoming || noteSeen || localGate.locked) return;
    noteSeen = true;
    // 打开时就是离线的：记不上，下次联网打开还会再出现一次。记上以后推送里有 note.read，足迹跟着出来
    if (opts.offlineSince === undefined) api.readNote(incoming.id).catch(() => {});
  };
  // 完整入场自己管背景音乐（爆炸时淡入）；另外两档第一次点击时开始放
  let introPlaying = plan?.mode === 'full';
  /** 入场放完了：「Ta 已赴约」的回响等这时再放 */
  let introDone: Promise<void> = Promise.resolve();
  // 离线缓存里的老快照可能还没有这一项
  const hold = space.introHold ?? LIMITS.introHold;
  if (plan?.mode === 'full') {
    appRoot.classList.add('intro-on');
    const view = mountIntro(shell.overlay, { title: snap.space.name, sub: '我们的每一段回忆，都是一颗星' });
    // 有 Ta 的暗号时那段话总是放（停留设成 0 也放），至少停到读得完
    const wordsHold = incoming ? Math.max(hold, noteHold(incoming.text)) : hold;
    const words =
      wordsHold > 0 ? { view: mountIntroWords(shell.overlay, { text: wordsText(), by: noteBy, dust, reduced: reducedMotion, onShow: seeNote }), hold: wordsHold } : undefined;
    introDone = playIntro(galaxy, view, sound, {
      reduced: reducedMotion,
      onReveal: () => {
        // Ta 刚加入的这一次：入场的最后一拍是两颗星的并轨，顶栏等它锁定（或落定）才出来——同纪念日掠影那段的做法
        if (!just) appRoot.classList.remove('intro-on');
      },
      words,
    }).then(() => {
      introPlaying = false;
    });
    if (just)
      introDone = introDone
        .then(() =>
          galaxy.playMerge({
            onLock: () => {
              sound.chime();
              shatterBuzz();
            },
          }),
        )
        .then(() => appRoot.classList.remove('intro-on'));
  } else if (plan?.mode === 'anniversary') {
    // 纪念日的话总是放（那一行就是这一档的意思），停留秒数用这一档自己的；有 Ta 的暗号时纪念日那行下面接它
    appRoot.classList.add('intro-on');
    const words = mountIntroWords(shell.overlay, { text: wordsText(), by: noteBy, dust, reduced: reducedMotion, onShow: seeNote });
    // 极光和寄语之后接纪念日掠影第二版（P4d，design §11.11）：沿纪念日的路线逐站掠过——整周年走金色的
    // 纪念日射线、生日走淡玫瑰色的生日射线（都是 semantic.ts 画的那两条）、满 100 天走最近 100 天的螺线。
    // 路线是纯几何，没有照片也照飞；生日缺月日（成员没填）建不出来，不放掠影。
    const occasion = plan.occasion;
    const bd =
      occasion?.kind === 'birthday'
        ? birthdays().find((b) => (occasion.both ? true : occasion.mine ? b.mine : b.name === occasion.name))
        : undefined;
    const route = occasion
      ? glimpseRoute({
          kind: occasion.kind,
          since: space.since,
          today,
          birthday: bd ? { monthDay: bd.monthDay, name: bd.name, mine: bd.mine, both: occasion?.kind === 'birthday' && occasion.both === true } : undefined,
          titleOf: (date) => {
            const i = galaxy.firstOnDay(date);
            return i >= 0 ? (memories.get(galaxy.photos[i]!.memoryId)?.title ?? null) : null;
          },
          hasPhotos: (date) => galaxy.hasDay(date),
        })
      : null;
    // 放掠影时顶栏等它结束（落地或被打断）才出来，不然寄语飘走时先出来、掠影开始又藏一次；
    // 不放掠影（减少动态效果、路线建不出来）照原来的时机出来；playGlimpse 没放成也在 resolve 时出来
    const willGlimpse = route !== null && !reducedMotion;
    introDone = playAnniversary(galaxy, words, {
      reduced: reducedMotion,
      onReveal: () => {
        if (!willGlimpse) appRoot.classList.remove('intro-on');
      },
      hold: incoming ? noteHold(incoming.text) : undefined,
    })
      .then(() =>
        playGlimpse(galaxy, route, {
          onStop: (_stop, last) => {
            // 到站轻轻一响、安卓震一下；最后一站换成钟声
            if (last) sound.chime();
            else sound.roamTick();
            tickBuzz();
          },
        }),
      )
      .then((landed) => {
        appRoot.classList.remove('intro-on');
        // 落地了请 Ta 再走一遍；减少动态效果时掠影没放，但漫游照样能用（每站一个固定机位），也提示。
        // 被打断（用户自己动了镜头）、没照片、只有一天时不提示
        if ((landed || reducedMotion) && roamable())
          roamEndToast = toast(shell.toasts, '今天再走一遍这段日子？', { action: { label: '时光漫游', onClick: () => startRoam() }, ms: 8000 });
      });
  } else if (plan?.mode === 'daily') {
    galaxy.bloom(0.8);
    if (incoming) {
      mountBanner(shell.overlay, incoming.text, noteHold(incoming.text), noteBy);
      seeNote();
    } else if (hold > 0) mountBanner(shell.overlay, introWords(), 2);
  }
  // Ta 刚加入但这次没有完整入场（?intro=0 之类）：进来就放并轨，顶栏照常
  if (just && plan?.mode !== 'full')
    void galaxy.playMerge({
      onLock: () => {
        sound.chime();
        shatterBuzz();
      },
    });
  // 点了「跳过」，暗号没来得及出现：入场放完以后在顶部补一条
  if (incoming)
    void introDone.then(() => {
      if (noteSeen) return;
      mountBanner(shell.overlay, incoming.text, noteHold(incoming.text), noteBy);
      seeNote();
    });
  if (plan) void introDone.then(() => saveIntroMemo(afterIntro(memo, plan.mode, plan.occasion, today)));
  // 浏览器要先有一次点击才能出声：第一次点击或按键时解锁并开始放背景音乐。
  // 入场期间由「轻触心跳」解锁，音乐从爆炸开始淡入
  const kick = () => {
    sound.unlock();
    if (introPlaying) return;
    sound.start(2.5);
    removeEventListener('pointerdown', kick, true);
    removeEventListener('keydown', kick, true);
  };
  addEventListener('pointerdown', kick, true);
  addEventListener('keydown', kick, true);

  const hud = mountHud(
    shell.overlay,
    {
      onHome: () => {
        exitRing(false);
        galaxy.select(-1, false);
        galaxy.frameAll(true);
      },
      onFiles: (files) => {
        if (offline) toast(shell.toasts, '现在连不上星海服务，连上以后再添照片');
        else void upload(files);
      },
      onInvite: () => openInvite(),
      onTrail: () => {
        trailOn = !trailOn;
        saveTrail(trailOn);
        galaxy.setTrail(trailOn);
        hud.setTrail(trailOn);
        toast(shell.toasts, trailOn ? '星轨已显示：按时间连起所有回忆' : '星轨已隐藏');
      },
      onRoam: (id) => startRoam(id),
      onSettings: () => openSettings(),
      onExport: () => openExport(),
      onLocalGate: () => localGate.openSettings(),
      onDevices: () => openDevices(),
      onCapsule: () => sealCapsule(),
      onNote: () => writeNote(),
      onConstellation: (id) => (id ? enterConstellation(id) : exitConstellation()),
      onDisguise: () => setDisguise(!galaxy.isDisguised),
      music: sound,
      haptics: canVibrate ? haptics : null,
      cache: { stats: offlineStats, clear: clearOffline },
    },
    { fake, stats: showStats },
  );
  /** 打开时连不上服务、显示的是离线缓存；实时连接接上后变回 false */
  let offline = opts.offlineSince !== undefined;
  let exporting: ReturnType<typeof exportDialog> | null = null;
  const openExport = () => {
    if (!me || exporting) return;
    if (galaxy.roaming) galaxy.stopRoam();
    const fromCache = offline;
    exporting = exportDialog({
      viewerId: me.member.id,
      offline: fromCache,
      loadSnapshot: () => fromCache ? Promise.resolve(currentSnapshot()) : api.snapshot(),
      loadPhoto: (id, signal) => fromCache ? api.photoBlob(id, 't512', signal) : api.exportPhotoBlob(id, signal),
      onClose: () => { exporting = null; },
    });
  };
  let offlineNote: ReturnType<typeof toast> | null = null;
  if (opts.offlineSince) {
    const d = new Date(opts.offlineSince);
    const at = `${d.getMonth() + 1} 月 ${d.getDate()} 日 ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    hud.setConnection('offline');
    offlineNote = toast(shell.toasts, `现在连不上星海服务，先显示 ${at} 同步的星海；连上以后会自动跟上。`, { sticky: true });
  }
  let trailOn = readTrail();
  galaxy.setTrail(trailOn);
  hud.setTrail(trailOn);
  hud.setTitle(snap.space.name);
  const refreshCounts = () => {
    const days = daysBetween(space.since, localDateOf(new Date())) + 1;
    hud.setSub(`${fake ? '压测模式 · 没有照片 · ' : ''}在一起的第 ${days.toLocaleString('zh-CN')} 天 · 已点亮 ${galaxy.count.toLocaleString('zh-CN')} 颗星`);
  };
  refreshCounts();
  setInterval(() => {
    const s = galaxy.stats;
    hud.setStats(`${s.fps.toFixed(0)} fps · ${s.frameMs.toFixed(1)} ms · ${s.stars.toLocaleString('zh-CN')} 颗星 · ${s.calls} 次绘制 · 画质 ${galaxy.qualityLevel}`);
  }, 500);

  // ── 照片面板 ──
  let loading: AbortController | null = null;
  const step = (delta: number) => {
    const i = galaxy.selectedIndex;
    const day = galaxy.sameDay(i);
    const k = day.indexOf(i);
    if (k < 0 || day.length < 2) return;
    tickBuzz();
    galaxy.select(day[(k + delta + day.length) % day.length]!);
  };
  /** 一颗星在面板和轮转字幕里显示的文字 */
  const describe = (i: number): PanelContent => {
    const p = galaxy.photos[i]!;
    const memory = memories.get(p.memoryId);
    const day = galaxy.sameDay(i);
    const d = new Date(`${p.date}T12:00:00`);
    const time = p.takenAt && /T(\d{2}:\d{2})/.exec(p.takenAt)?.[1];
    const nth = daysBetween(space.since, p.date) + 1;
    return {
      date: `${d.getFullYear()} 年 ${d.getMonth() + 1} 月 ${d.getDate()} 日 · 星期${WEEKDAY.charAt(d.getDay())}${time ? ` · ${time}` : ''}`,
      title: memory?.title ?? (nth > 0 ? `在一起的第 ${nth} 天` : '认识之前'),
      place: memory?.place?.name ?? (p.lat !== undefined && p.lng !== undefined ? coordinates(p.lat, p.lng) : ''),
      story: memory?.story ?? '',
      by: `由 ${members.get(p.createdBy)?.displayName ?? 'Ta'} 点亮${memory?.title && nth > 0 ? ` · 在一起的第 ${nth} 天` : ''}`,
      count: `${day.indexOf(i) + 1} / ${day.length}`,
      aspect: p.width && p.height ? p.width / p.height : 4 / 3,
      accent: p.color,
      placeholder: p.thumbhash ? thumbHashToDataURL(Uint8Array.from(atob(p.thumbhash), (c) => c.charCodeAt(0))) : undefined,
      swatch: fake ? `radial-gradient(circle at 30% 30%, ${p.color}, #0b0d1a)` : undefined,
      note: fake ? '压测数据没有照片' : p.thumbs ? undefined : 'HEIC 原图已经存好，服务端暂时解不开，缩略图以后补上',
      editable: !fake,
      card: me ? cardOf(p, me.member) : undefined,
      themes: themesOfDay(p.memoryId),
      station: stationOf(p.date),
      // 正在给邀请挑照片：面板里多一个主按钮「就用这张邀请 Ta」
      action: invitePick && !fake ? { label: '就用这张邀请 Ta', onClick: () => finishInvitePick(p.id) } : undefined,
    };
  };
  /** 星座里这一天是第几站（不是站、没在看星座时没有） */
  const stationOf = (date: LocalDate) => {
    if (!constellation) return undefined;
    const s = stations(constellation), index = stationIndex(s, date);
    return index ? { index, total: s.length } : undefined;
  };
  /** 这张照片翻牌的两面（flip.ts） */
  const cardOf = (p: PhotoDto, self: MemberDto) => {
    const other = [...members.values()].find((m) => m.id !== self.id);
    return cardFaces({
      me: self.id,
      myName: members.get(self.id)?.displayName ?? self.displayName,
      uploader: p.createdBy,
      partner: other ? { id: other.id, name: other.displayName } : null,
      perspectives: perspectives.get(p.id) ?? [],
      voices: voices.get(p.id) ?? [],
    });
  };

  // ── 轮转：这一天的照片排成光环 ──
  const caption = mountCaption(shell.overlay, { onBack: () => exitRing(true) });
  /** 光环上是哪些星（其中一张被删了就收起光环） */
  let ringDay: number[] = [];
  /** 从光环回到面板时不放星尘：照片刚才就在眼前 */
  let quietOpen = false;
  const cardPlaceholder = async (i: number): Promise<CardImage | null> => {
    const p = galaxy.photos[i]!;
    if (fake) return swatchCanvas(p.color);
    if (!p.thumbhash) return null;
    const img = new Image();
    img.src = thumbHashToDataURL(Uint8Array.from(atob(p.thumbhash), (c) => c.charCodeAt(0)));
    await img.decode();
    return img;
  };
  const cardImage = async (i: number, signal: AbortSignal): Promise<CardImage | null> => {
    const p = galaxy.photos[i]!;
    if (fake || !p.thumbs) return null;
    const url = URL.createObjectURL(await api.photoBlob(p.id, 't512', signal));
    const img = new Image();
    img.src = url;
    try {
      await img.decode();
    } catch (e) {
      URL.revokeObjectURL(url);
      throw e;
    }
    return img;
  };
  const enterRing = () => {
    const i = galaxy.selectedIndex;
    if (i < 0 || galaxy.inCarousel) return;
    const day = galaxy.sameDay(i);
    if (day.length < 2) return;
    closeEditor(true);
    panel.hide({ quiet: true });
    constelBar.hide();
    photoFocus(true, 'ring');
    appRoot.classList.add('ring-on');
    ringDay = day;
    galaxy.enterCarousel(day, day.indexOf(i), {
      placeholder: cardPlaceholder,
      load: cardImage,
      onFront: (j, k, n) => {
        const c = describe(j);
        caption.show({ date: c.date, title: c.title, place: c.place, count: `${k + 1} / ${n}` });
        // 转过一格，轻轻敲一下（安卓）
        tickBuzz();
      },
      onOpen: () => exitRing(true),
    });
  };
  /** 收起光环；open 时回到正面那张的照片面板 */
  const exitRing = (open: boolean) => {
    if (!galaxy.inCarousel) return;
    const i = galaxy.leaveCarousel();
    photoFocus(false, 'ring');
    ringDay = [];
    caption.hide();
    appRoot.classList.remove('ring-on');
    refreshConstelBar();
    if (open && i >= 0) {
      quietOpen = true;
      galaxy.select(i);
    }
  };
  /** 飞到某颗星（提示里的「去看看」）；在光环里时先收起 */
  const goTo = (i: number) => {
    // 漫游中点了提示里的「去看看」：先退出漫游，再去看那张
    if (galaxy.roaming) galaxy.stopRoam();
    exitRing(false);
    galaxy.select(i);
  };

  const panel = mountPanel(
    shell.overlay,
    {
      onClose: () => galaxy.select(-1, false),
      onPrev: () => step(-1),
      onNext: () => step(1),
      onRing: enterRing,
      onEdit: (focus) => openEditor(focus),
      onDelete: () => void deleteShown(),
      onTheme: (id) => (id === constellation ? exitConstellation() : enterConstellation(id)),
      onThemes: () => openThemes(),
      onStation: (dir) => stepConstellation(dir),
      onSaveSide: (text, base) => saveSide(text, base),
      onVoice: (memberId) => openVoice(memberId),
      onFaceChange: closeVoice,
      onPhotoFocus: photoFocus,
    },
    { morphPoints: lowEnd ? 24576 : 65536, dust, reduced: reducedMotion },
  );
  const narrow = matchMedia('(max-width: 640px)');
  // 漫游中窗口宽窄变了：宽屏时卡片占着上半、星团让到下半（-0.25）；窄屏不让（同开始漫游时的规则）
  narrow.addEventListener('change', () => {
    if (galaxy.roaming) galaxy.setViewShift(narrow.matches ? 0 : -0.25);
  });
  /** 面板里正显示的那颗星 */
  let shown = -1;
  galaxy.onSelect = (i) => {
    // 打开一张照片时收起时间胶囊的面板（两个同时只开一个）
    if (i >= 0) closeCapsule();
    // 换到了别的回忆的照片、或者关了面板：编辑到一半的先收起来，下次打开接着写
    if (editing && (i < 0 || galaxy.photos[i]?.memoryId !== editing.memoryId)) closeEditor(true);
    loading?.abort();
    loading = null;
    galaxy.setViewShift(i >= 0 && narrow.matches ? 0.24 : 0);
    const before = shown;
    shown = i;
    if (i >= 0 && constellation) stationDay = galaxy.photos[i]!.date;
    refreshConstelBar(i);
    const quiet = quietOpen;
    quietOpen = false;
    if (i < 0) return panel.hide();
    const p = galaxy.photos[i]!;
    // 面板开着时换到另一张：等清晰图到了用流体形变换过去
    const sw = panel.open && before >= 0 && before !== i && !fake && p.thumbs ? { phase: (before * 1.71 + i * 0.93) % 10 } : undefined;
    // 新打开时星尘从星里飞进画框；从光环回来时照片刚才就在眼前，直接显示
    panel.show(describe(i), { sw, from: sw || quiet ? undefined : () => galaxy.screenPosition(i) });
    if (!fake && p.thumbs) {
      const ctrl = (loading = new AbortController());
      api
        .photoBlob(p.id, 't512', ctrl.signal)
        .then((b) => {
          if (galaxy.selectedIndex === i) panel.setImage(b);
        })
        .catch(() => undefined);
    }
  };

  // ── 邀请（P3a，design §11.12）：邀请 Ta 附一颗星和一句话；每次打开都先问服务端 ──
  /** 正在给邀请挑照片时不为 null：面板多一个「就用这张邀请 Ta」，提示条上的「取消」和 Esc 都能退出 */
  let invitePick: { draft: InviteDraft; note: ToastHandle } | null = null;
  const openInvite = (draft?: InviteDraft, start?: 'recover' | 'device') => {
    // 还在的照片（回收站不算）；附的照片只能从这里面挑
    const live = galaxy.livePhotos();
    inviteDialog({
      canInviteMember: members.size < 2,
      list: () => api.invites().then((r) => r.invites),
      create: (req) => api.invite(req),
      revoke: (id) => api.revokeInvite(id),
      photoOf: (id) => live.find((p) => p.id === id) ?? null,
      thumb: (id) => api.photoBlob(id, 't128'),
      first: inviteDefaults(live),
      latest: latestPhoto(live),
      canPick: !galaxy.isDisguised,
      pick: (d) => {
        // 去星海里挑一颗：先退出漫游、轮转、星座，免得这些画面和挑照片打架
        if (galaxy.roaming) galaxy.stopRoam();
        exitRing(false);
        if (constellation) exitConstellation();
        invitePick = {
          draft: d,
          // 触屏没有 Esc：提示不自动消失，上面有「取消」
          note: toast(shell.toasts, '点一颗星，看看是不是这张', { sticky: true, action: { label: '取消', onClick: () => cancelInvitePick(true) } }),
        };
      },
      draft,
      start,
    });
  };

  // ── 我的设备（ADR-0016）：改名、移除；Ta 的设备都丢了时帮 Ta 生成找回码 ──
  const openDevices = () => {
    if (offline) return void toast(shell.toasts, '现在连不上星海服务，连上以后再管设备');
    const partner = partnerOf();
    devicesDialog({
      list: () => api.devices().then((r) => r.devices),
      rename: (id, label) => api.renameDevice(id, label).then((r) => r.device),
      remove: (id) => api.removeDevice(id),
      nameOf,
      removedSelf: () => void forget().then(() => location.reload()),
      addDevice: () => openInvite(undefined, 'device'),
      recoverPartner: partner ? () => openInvite(undefined, 'recover') : null,
      partnerName: partner?.displayName ?? null,
    });
  };
  const endInvitePick = () => {
    const p = invitePick;
    invitePick = null;
    p?.note.close();
    return p?.draft ?? null;
  };
  /** 取消挑照片：关面板、回对话框，照片还是原来那张 */
  const cancelInvitePick = (back: boolean) => {
    const d = endInvitePick();
    if (!d) return;
    if (galaxy.selectedIndex >= 0) galaxy.select(-1, false);
    if (back) openInvite(d);
  };
  const finishInvitePick = (id: string) => {
    const d = endInvitePick();
    if (!d) return;
    galaxy.select(-1, false);
    openInvite({ photoId: id, message: d.message });
  };

  addEventListener('keydown', (e) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    // 对话框（邀请、星海设置）里的按键归对话框
    if (e.target instanceof Element && e.target.closest('dialog')) return;
    // 并轨进行中：Esc / 回车 / 空格直接落到锁定后的绕转，这一下不再做别的（轻触画面跳过是 galaxy 自己做的）
    if (galaxy.twinState.merging && (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      return galaxy.finishMerge();
    }
    // B：藏起照片 / 恢复（防借看）
    if ((e.key === 'b' || e.key === 'B') && !e.metaKey && !e.ctrlKey && !e.altKey) return setDisguise(!galaxy.isDisguised);
    // T：时光漫游——没在漫游时开始（在星座里是漫游这个星座；在轮转里按也会先收起轮转），在漫游时退出。
    // 入场还没放完时按了什么都不做：镜头、画面都不是漫游的
    if ((e.key === 't' || e.key === 'T') && !e.metaKey && !e.ctrlKey && !e.altKey) {
      if (galaxy.roaming) galaxy.stopRoam();
      else if (!introPlaying) startRoam(constellation);
      return;
    }
    // 纪念日掠影中 Esc 是「收」（同轻触）：掠影自己的捕获监听先处理正常按键，这里兜住直接派发到 window 上的
    // 合成事件——那时本监听（先注册的）会先跑。要是漏到下面的 stepBack，掠影会被接管、落地提示就不出了
    if (e.key === 'Escape' && galaxy.glimpsing) return galaxy.endGlimpse();
    // 正在给邀请挑照片：Esc 退出挑照片、回到邀请对话框（触屏没有 Esc，用提示条上的「取消」）
    if (e.key === 'Escape' && invitePick) return cancelInvitePick(true);
    // 漫游中：← → 换站，空格暂停 / 继续（preventDefault，免得页面滚动、按到按钮）；Esc 先关胶囊面板，否则退出漫游（T 在上面已经退了）
    if (galaxy.roaming) {
      if (e.key === 'Escape') {
        if (capsulePanel.open) closeCapsule();
        else galaxy.stopRoam();
      } else if (e.key === 'ArrowLeft') seekRoam(-1);
      else if (e.key === 'ArrowRight') seekRoam(1);
      else if (e.key === ' ') {
        e.preventDefault();
        toggleRoamPause();
      }
      return;
    }
    if (galaxy.inCarousel) {
      // 回车只在没有按钮获得焦点时算「打开正面那张」，免得和按钮自己的回车撞上（同原型）
      if (e.key === 'Escape' || (e.key === 'Enter' && (e.target === document.body || e.target === galaxy.renderer.domElement))) exitRing(true);
      else if (e.key === 'ArrowLeft') galaxy.carouselStep(-1);
      else if (e.key === 'ArrowRight') galaxy.carouselStep(1);
      else if (e.key === 'Home') {
        exitRing(false);
        galaxy.frameAll(true);
      }
      return;
    }
    // 空格、F：照片翻面（双面翻牌）；焦点在按钮上时空格是按按钮，写字时是打字
    if (panel.open && !panel.writingSide && (e.key === ' ' || e.key === 'f' || e.key === 'F') && !(e.target instanceof HTMLButtonElement)) {
      e.preventDefault();
      return panel.flip();
    }
    if (e.key === 'Escape' && capsulePanel.open) closeCapsule();
    else if (e.key === 'Escape' && panel.open) galaxy.select(-1, false);
    // 在星座里：面板关着时 Esc 退出星座
    else if (e.key === 'Escape' && constellation) exitConstellation();
    // 没开着照片：Esc 一层一层往外退，一天 → 一年 → 整片星海
    else if (e.key === 'Escape') galaxy.stepBack();
    // 星座里换站：← → 已经是面板里的上一张 / 下一张
    else if (constellation && (e.key === '[' || e.key === ']')) stepConstellation(e.key === ']' ? 1 : -1);
    else if (e.key === 'Home') galaxy.frameAll(true);
    else if (panel.open && e.key === 'ArrowLeft') step(-1);
    else if (panel.open && e.key === 'ArrowRight') step(1);
  });

  // 有变化时过几秒存一份新的快照，下次连不上服务时显示的就是最新的
  let seq = snap.seq;
  const currentSnapshot = (): SnapshotResponse => ({
    seq, space,
    members: [...members.values()],
    memories: [...memories.values()].map((m) => ({ ...m, themeIds: memoryThemes.has(m.id) ? [...memoryThemes.get(m.id)!] : undefined })),
    photos: galaxy.livePhotos(),
    perspectives: [...perspectives.values()].flat(),
    voices: [...voices.values()].flat(),
    capsules: [...capsules.values()],
    notes: [...notes.values()],
    themes: [...themes.values()],
  });
  let saveTimer: ReturnType<typeof setTimeout> | undefined;
  const remember = () => {
    if (!me) return;
    clearTimeout(saveTimer);
    saveTimer = setTimeout(
      () =>
        void saveSnapshot(
          { ...me, space },
          currentSnapshot(),
        ),
      3000,
    );
  };

  const nameOf = (id: string) => members.get(id)?.displayName ?? 'Ta';
  /** 成员的资料变了（生日）：生日射线跟着变，下次打开时的纪念日入场、胶囊的解封日子也按新的算 */
  const applyMember = (m: MemberDto) => {
    members.set(m.id, m);
    galaxy.setBirthdays(birthdayLabels());
    remember();
  };
  /** 改东西的是谁：「小满 」或者「你在另一台设备上」；mid 是放在句子中间（名字两边都空一格，同「由 小满 点亮」） */
  const actor = (id: string, mid = false) => (id === me?.member.id ? '你在另一台设备上' : mid ? ` ${nameOf(id)} ` : `${nameOf(id)} `);

  // ── 编辑回忆（edit.ts）──
  interface Editing {
    memoryId: string;
    /** 打开编辑时看到的版本；撞上 Ta 刚改过、合并以后换成最新的 */
    base: MemoryDto;
    form: MemoryEditor;
    saving: boolean;
  }
  let editing: Editing | null = null;
  /** 没保存就被打断（点了别的星、关了面板、进了轮转）的修改：再打开这段回忆的编辑时接着写 */
  const drafts = new Map<string, { base: MemoryDto; values: MemoryFields }>();

  /**
   * 回忆来了或者改了（自己保存的、实时推送的、上传带回来的）。推送可能比保存的回应晚到，
   * 旧版本不能盖掉新版本：updatedAt 是服务端给的，同一段回忆每改一次都更晚。
   */
  const applyMemory = (m: MemoryDto): boolean => {
    const old = memories.get(m.id);
    if (old && old.updatedAt > m.updatedAt) return false;
    memories.set(m.id, m);
    galaxy.setMemory(m);
    // 星座里的站名用回忆的标题（「3 · 厦门」）
    if (constellation && old?.title !== m.title) afterThemes(false);
    const i = galaxy.selectedIndex;
    if (i >= 0 && galaxy.photos[i]?.memoryId === m.id) panel.text(describe(i));
    // 正在编辑这段回忆、又不是自己这次保存的：还没动过表单就换成最新的，动过了先提醒一句
    const e = editing;
    if (e && e.memoryId === m.id && !e.saving && m.updatedAt !== e.base.updatedAt) {
      if (isEmpty(changesOf(fieldsOf(e.base), e.form.values()))) {
        e.base = m;
        e.form.set(fieldsOf(m));
        e.form.note(`${actor(m.updatedBy)}刚改过这段回忆，表单里已经是最新的`);
      } else e.form.note(`${actor(m.updatedBy)}刚刚也改了这段回忆；保存时要是和你改的地方撞上，会先给你看改了什么`);
    }
    return true;
  };

  const openEditor = (focus?: FieldName) => {
    const i = galaxy.selectedIndex, p = galaxy.photos[i], m = p && memories.get(p.memoryId);
    if (fake || !p || !m || editing) return;
    if (offline) return void toast(shell.toasts, '现在连不上星海服务，连上以后再写');
    const draft = drafts.get(m.id);
    drafts.delete(m.id);
    const nth = daysBetween(space.since, p.date) + 1;
    const form = memoryEditor({
      values: draft?.values ?? fieldsOf(m),
      placeholders: { title: nth > 0 ? `在一起的第 ${nth} 天` : '认识之前', place: p.lat !== undefined && p.lng !== undefined ? coordinates(p.lat, p.lng) : '' },
      note: draft ? `接着上次没保存的修改${draft.base.updatedAt !== m.updatedAt ? `；这期间${actor(m.updatedBy, true)}也改过，保存时会先给你看改了什么` : ''}` : undefined,
      dirty: (v) => !isEmpty(changesOf(fieldsOf(e.base), v)),
      onSave: () => void save(e),
      onCancel: () => closeEditor(false),
    });
    const e: Editing = { memoryId: m.id, base: draft?.base ?? m, form, saving: false };
    editing = e;
    panel.edit(form.node);
    form.focus(focus);
  };

  /** stash：被打断的，把没保存的修改收起来 */
  const closeEditor = (stash: boolean) => {
    const e = editing;
    if (!e) return;
    editing = null;
    const values = e.form.values();
    if (stash && !isEmpty(changesOf(fieldsOf(e.base), values))) drafts.set(e.memoryId, { base: e.base, values });
    panel.edit(null);
  };

  // ── 防借看伪装（改造需求「一键防尴尬伪装」）：只剩星点和天幕慢慢转，照片、文字、按钮都藏起来，声音也停 ──
  // 进入：长按顶栏标题、三指轻触、按 B；恢复：三指轻触、按 B，或者在画面上按住不动 1.2 秒（标题这时也藏起来了）
  const setDisguise = (on: boolean) => {
    if (galaxy.isDisguised === on) return;
    if (on) {
      closeVoice();
      exporting?.close();
      exitRing(false);
      // 漫游中进伪装：漫游停下（onStop(false)，不提示）
      if (galaxy.roaming) galaxy.stopRoam();
      closeEditor(true);
      closeCapsule();
      exitConstellation();
      // 正在给邀请挑照片也退掉：伪装时星海里点不出面板，挑不下去了（不回对话框）
      cancelInvitePick(false);
    }
    galaxy.setDisguise(on);
    appRoot.classList.toggle('disguise', on);
    sound.hush(on);
    tickBuzz();
  };
  onGateLock = () => {
    closeVoice(); exporting?.close();
    if (galaxy.roaming) galaxy.stopRoam();
    closeEditor(true); closeCapsule(); exitRing(false);
    galaxy.select(-1, false);
    panel.hide({ quiet: true });
  };
  let threeAt = 0;
  addEventListener(
    'touchstart',
    (e) => {
      if (e.touches.length !== 3 || performance.now() - threeAt < 800) return;
      threeAt = performance.now();
      setDisguise(!galaxy.isDisguised);
    },
    { passive: true },
  );
  let unhide: { x: number; y: number; timer: number } | null = null;
  const stopUnhide = () => {
    if (unhide) clearTimeout(unhide.timer);
    unhide = null;
  };
  addEventListener(
    'pointerdown',
    (e) => {
      stopUnhide();
      if (!galaxy.isDisguised || !e.isPrimary) return;
      unhide = { x: e.clientX, y: e.clientY, timer: window.setTimeout(() => setDisguise(false), 1200) };
    },
    true,
  );
  addEventListener('pointermove', (e) => unhide && Math.hypot(e.clientX - unhide.x, e.clientY - unhide.y) > 10 && stopUnhide(), true);
  addEventListener('pointerup', stopUnhide, true);
  addEventListener('pointercancel', stopUnhide, true);

  const finish = (e: Editing, m: MemoryDto) => {
    applyMemory(m);
    remember();
    drafts.delete(e.memoryId);
    if (editing === e) closeEditor(false);
  };

  /** 合并到 latest 上：我没动过的字段换成最新的，免得保存时把 Ta 改的又改回去 */
  const rebaseForm = (e: Editing, latest: MemoryDto, mine: FieldChanges) => {
    const theirs = fieldsOf(latest);
    e.form.set(Object.fromEntries(FIELD_NAMES.filter((k) => mine[k] === undefined).map((k) => [k, theirs[k]])));
    e.base = latest;
  };

  /** 同一个字段两个人写得不一样：给人看 Ta 写的，选用谁的 */
  const askConflict = (e: Editing, latest: MemoryDto, mine: FieldChanges, conflicts: FieldName[]) => {
    // 表单已经收起来了：草稿还在，下次打开时会提醒
    if (editing !== e) return;
    const theirs = fieldsOf(latest), who = actor(latest.updatedBy);
    e.form.conflict({
      text: `${who}刚刚也改了${conflicts.map((k) => `「${FIELD_LABELS[k]}」`).join('')}，和你写的不一样。下面是${latest.updatedBy === me?.member.id ? '那台设备上' : ' Ta '}写的，你写的还在表单里。`,
      theirs: conflicts.map((k) => ({ label: FIELD_LABELS[k], value: String(theirs[k]) })),
      onMine: () => {
        e.form.conflict(null);
        rebaseForm(e, latest, mine);
        void save(e);
      },
      onTheirs: () => {
        e.form.conflict(null);
        rebaseForm(e, latest, mine);
        e.form.set(Object.fromEntries(conflicts.map((k) => [k, theirs[k]])));
        e.form.note('换成了 Ta 写的；你改的别的地方还在，看过没问题再保存');
      },
    });
  };

  const save = async (e: Editing) => {
    if (e.saving) return;
    let base = e.base, mine = changesOf(fieldsOf(base), e.form.values());
    if (isEmpty(mine)) return closeEditor(false);
    e.saving = true;
    e.form.busy(true);
    e.form.error('');
    try {
      // 撞上 Ta 改的是别的字段：在最新版本上自动重试；连着撞三次就停下，让人再点一次
      for (let attempt = 0; ; attempt++) {
        try {
          return finish(e, (await api.updateMemory(e.memoryId, toPatch(base.updatedAt, mine))).memory);
        } catch (err) {
          const latest = conflictOf(err);
          if (!latest || attempt >= 2) throw err;
          applyMemory(latest);
          const r = rebase(fieldsOf(base), mine, fieldsOf(latest));
          if (isEmpty(r.changes)) return finish(e, latest);
          if (r.conflicts.length) return askConflict(e, latest, mine, r.conflicts);
          base = latest;
          mine = r.changes;
        }
      }
    } catch (err) {
      e.form.error(err instanceof ApiError && err.status === 0 ? '网络断了，没保存上；连上以后再点一次保存' : err instanceof Error ? err.message : String(err));
    } finally {
      e.saving = false;
      e.form.busy(false);
    }
  };

  // ── 双面翻牌（ADR-0011）：每张照片两个人各一面 ──
  /** 两个人都写了：星海里这颗星外面多一圈双光环 */
  const markResonance = (photoId: string) => {
    const list = perspectives.get(photoId) ?? [];
    galaxy.setMark(photoId, new Set(list.map((x) => x.memberId)).size >= 2 ? 1 : 0);
  };
  /** 一张照片的视角换成新的（自己保存的、推送来的）：正看着它就刷新面板 */
  const applyPerspectives = (photoId: string, list: PerspectiveDto[]) => {
    if (list.length) perspectives.set(photoId, list);
    else perspectives.delete(photoId);
    markResonance(photoId);
    const i = galaxy.selectedIndex;
    if (i >= 0 && galaxy.photos[i]?.id === photoId) panel.text(describe(i));
    remember();
  };
  for (const id of perspectives.keys()) markResonance(id);

  /**
   * 保存自己在正显示的这张照片上的那一面；base 是打开表单时的版本号（不是现在的：这期间另一台设备改了，
   * 推送会把本地的换成最新的，拿它当 base 就会悄悄盖掉那边写的）。失败时抛出的错误显示在翻牌的表单下面
   */
  const saveSide = async (text: string | null, base: string | null) => {
    const p = galaxy.photos[galaxy.selectedIndex];
    if (!p || !me) throw new Error('这张照片不见了');
    if (offline) throw new Error('现在连不上星海服务，连上以后再写');
    try {
      const res = await api.writePerspective(p.id, { text, base });
      applyPerspectives(res.photoId, res.perspectives);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.body && typeof e.body === 'object' && 'perspective' in e.body) {
        // 另一台设备刚改过：框里的字不动，记下那边的版本号，再点保存就用这次写的
        const latest = (e.body as PerspectiveConflictBody).perspective, others = (perspectives.get(p.id) ?? []).filter((x) => x.memberId !== me.member.id);
        applyPerspectives(p.id, latest ? [...others, latest] : others);
        throw new SideConflict(
          latest
            ? `你在另一台设备上刚改成了「${(latest.text ?? '').replace(/[。！？.!?…]+$/, '')}」。框里还是这次写的，再点保存就用它`
            : '你在另一台设备上刚把这一面清空了。框里还是这次写的，再点保存就用它',
          latest?.updatedAt ?? null,
        );
      }
      throw e instanceof ApiError && e.status === 0 ? new Error('网络断了，没保存上；连上以后再点一次保存') : e;
    }
  };

  // 录音有独立版本和序号：文字变更不覆盖录音，迟到的响应也不能恢复已隐藏的声音。
  const applyVoices = (photoId: string, next: VoiceDto[], at: number) => {
    if (at <= Math.max(voiceResetSeq, voiceVersions.get(photoId) ?? 0)) return;
    voiceVersions.set(photoId, at);
    if (next.length) voices.set(photoId, next); else voices.delete(photoId);
    if (voiceView?.photoId === photoId && voiceView.memberId !== me?.member.id) closeVoice();
    const i = galaxy.selectedIndex;
    if (i >= 0 && galaxy.photos[i]?.id === photoId) panel.text(describe(i));
    remember();
  };
  const openVoice = (memberId: string) => {
    const photo = galaxy.photos[galaxy.selectedIndex];
    if (!photo || !me || localGate.locked) return;
    closeVoice();
    const mine = memberId === me.member.id;
    const voice = voices.get(photo.id)?.find((v) => v.memberId === memberId) ?? null;
    if (!mine && !voice) return;
    const mutate = async (action: () => Promise<VoicesResponse>) => {
      try {
        const response = await action();
        applyVoices(response.photoId, response.voices, response.seq);
      } catch (e) {
        if (e instanceof ApiError && e.status === 409 && e.body && typeof e.body === 'object' && 'voice' in e.body) {
          const latest = (e.body as VoiceConflictBody).voice;
          throw new VoiceConflict('另一台设备刚改过这段声音。这次录音还在，再点保存或删除，将使用这次的选择。', latest?.updatedAt ?? null);
        }
        throw e;
      }
    };
    const view = voiceDialog({
      mine, voice, name: members.get(memberId)?.displayName ?? 'Ta', online: !offline, sound,
      load: (signal) => api.voiceBlob(photo.id, memberId, signal),
      save: (blob, base, signal) => mutate(() => api.saveVoice(photo.id, blob, base, signal)),
      remove: (base, signal) => mutate(() => api.deleteVoice(photo.id, base, signal)),
      onClose: () => { voiceView = null; },
    });
    voiceView = { ...view, photoId: photo.id, memberId };
  };

  // ── 时间胶囊（ADR-0011）：星茧悬在解封那天的上方；胶囊面板和照片面板一个位置，同时只开一个 ──
  const capsules = new Map<string, CapsuleDto>((snap.capsules ?? []).map((c) => [c.id, c]));
  const PHASE: Record<CapsulePhase, number> = { sealed: 0, warming: 1, ready: 2, opened: 3 };
  const myId = me?.member.id ?? '';
  const partnerOf = () => [...members.values()].find((m) => m.id !== myId);
  const refreshCocoons = () => galaxy.setCapsules([...capsules.values()].map((c) => ({ id: c.id, date: c.date, phase: PHASE[phaseOf(c, myId, Date.now())] })));
  const capsuleView = (c: CapsuleDto): CapsuleView => {
    const p = c.photo;
    return {
      capsule: c,
      phase: phaseOf(c, myId, Date.now()),
      mine: c.createdBy === myId,
      creatorName: c.createdBy === myId ? '你' : nameOf(c.createdBy),
      partnerName: partnerOf()?.displayName ?? 'Ta',
      countdown: countdown(c.unlockAt, Date.now()),
      photoBlob: p?.thumbs ? () => api.photoBlob(p.id, 't512') : undefined,
      placeholder: p?.thumbhash ? thumbHashToDataURL(Uint8Array.from(atob(p.thumbhash), (ch) => ch.charCodeAt(0))) : undefined,
    };
  };
  /** 胶囊来了或者变了（自己的回应、推送来的）：星茧换阶段，正看着它就刷新面板 */
  const applyCapsule = (c: CapsuleDto) => {
    capsules.set(c.id, c);
    refreshCocoons();
    if (capsulePanel.id === c.id) capsulePanel.show(capsuleView(c));
    remember();
  };
  /** 长按时的低音和由弱渐强的震动；没按满就松手、或者按满了，都停下 */
  let charge: (() => void) | null = null;
  let chargeTimer = 0;
  const stopCharge = () => {
    charge?.();
    charge = null;
    clearInterval(chargeTimer);
    chargeTimer = 0;
    stopBuzz();
  };
  /** 对着正显示的那颗胶囊做一件事；离线时不做 */
  const onShown = async (verb: string, work: (id: string) => Promise<void>) => {
    const id = capsulePanel.id;
    if (!id) return;
    if (offline) throw new Error(`现在连不上星海服务，连上以后再${verb}`);
    await work(id);
  };
  const capsulePanel = mountCapsulePanel(
    shell.overlay,
    {
      onClose: () => closeCapsule(),
      onPress: (k) => {
        const id = capsulePanel.id;
        if (!id) return;
        if (k < 0) {
          stopCharge();
          galaxy.pressCapsule(id, 0);
          return;
        }
        if (!charge) {
          sound.unlock();
          charge = sound.charge(2);
          chargeBuzz();
          chargeTimer = window.setInterval(chargeBuzz, 1000);
        }
        galaxy.pressCapsule(id, k);
      },
      onOpen: () =>
        onShown('拆', async (id) => {
          stopCharge();
          let res;
          try {
            res = await api.openCapsule(id);
          } finally {
            galaxy.pressCapsule(id, 0);
          }
          applyCapsule(res.capsule);
          // 碎开：冲击波、冰晶碎裂声、一下重的震动；照片从星茧的位置化成星尘飞进来
          galaxy.shatterCapsule(id);
          sound.shatter();
          shatterBuzz();
          capsulePanel.show(capsuleView(res.capsule), { reveal: () => galaxy.capsuleScreenPosition(id) });
        }),
      onPeek: () => onShown('看', async (id) => applyCapsule((await api.peekCapsule(id)).capsule)),
      onLetter: (text) => onShown('改', async (id) => applyCapsule((await api.updateCapsule(id, { letter: text })).capsule)),
      onDate: (date) => onShown('改', async (id) => applyCapsule((await api.updateCapsule(id, { unlockAt: midnightOf(date), date })).capsule)),
      onWithdraw: (keep) =>
        onShown('撤回', async (id) => {
          const photoId = capsules.get(id)?.photo?.id;
          await api.removeCapsule(id, keep);
          if (photoId && !keep) void forgetThumbs(photoId);
          capsules.delete(id);
          refreshCocoons();
          closeCapsule();
          remember();
          toast(shell.toasts, keep ? '时间胶囊撤回了，照片回到了它拍摄的那天' : '时间胶囊撤回了');
        }),
    },
    { dust, reduced: reducedMotion },
  );
  const showCapsule = (id: string) => {
    const c = capsules.get(id);
    if (!c) return;
    exitRing(false);
    galaxy.select(-1, false);
    capsulePanel.show(capsuleView(c));
    photoFocus(Boolean(capsuleView(c).photoBlob), 'capsule');
    galaxy.setViewShift(narrow.matches ? 0.24 : 0);
  };
  const closeCapsule = () => {
    if (!capsulePanel.open) return;
    stopCharge();
    capsulePanel.hide();
    photoFocus(false, 'capsule');
    if (galaxy.selectedIndex < 0) galaxy.setViewShift(0);
  };
  galaxy.onCapsule = (id) => showCapsule(id);
  refreshCocoons();
  // 倒计时和阶段跟着时间走：封印 → 最后 24 小时 → 到了
  setInterval(() => {
    if (!capsules.size) return;
    refreshCocoons();
    const id = capsulePanel.id, c = id ? capsules.get(id) : undefined;
    if (c) capsulePanel.show(capsuleView(c));
  }, 20_000);
  const sealCapsule = () => {
    if (offline) return void toast(shell.toasts, '现在连不上星海服务，连上以后再封存');
    capsuleDialog({
      presets: unlockPresets(space.since, localDateOf(new Date()), birthdayLabels()),
      partnerName: partnerOf()?.displayName ?? 'Ta',
      save: async (v) => {
        const form = v.file ? await uploadForm(v.file) : new FormData();
        form.set(CAPSULE_FIELD.kind, v.kind);
        if (v.letter.trim()) form.set(CAPSULE_FIELD.letter, v.letter);
        form.set(CAPSULE_FIELD.unlockAt, midnightOf(v.date));
        form.set(CAPSULE_FIELD.date, v.date);
        const res = await api.sealCapsule(form);
        applyCapsule(res.capsule);
        const id = res.capsule.id;
        toast(shell.toasts, `时间胶囊封好了，${dayText(v.date)}解封`, {
          action: {
            label: '去看看',
            onClick: () => {
              galaxy.focusCapsule(id);
              showCapsule(id);
            },
          },
          ms: 9000,
        });
      },
    });
  };

  // ── 主题星座（ADR-0011，design §11.10，themes.ts）：给回忆打主题；选一个主题，同一主题的日子按时间连成一条线 ──
  const stations = (themeId: string) => stationsOf(themeId, memoryThemes, (id) => memories.get(id)?.date, (d) => galaxy.hasDay(d));
  const constelBar = mountConstellationBar(shell.overlay, { onPrev: () => stepConstellation(-1), onNext: () => stepConstellation(1), onExit: () => exitConstellation() });
  const refreshStarmap = () => {
    hud.setStarmap(
      [...themes.values()].map((t) => ({ id: t.id, name: t.name, count: stations(t.id).length })),
      constellation,
    );
    // 漫游的入口：整片星海要有照片的日子不少于两天才能点；正在看的星座自己的两站下限在菜单里算
    hud.setRoamable(roamable());
  };
  /** 底部字幕条：星座里、没在看照片、没在轮转时才有 */
  const refreshConstelBar = (selected = galaxy.selectedIndex) => {
    const t = constellation ? themes.get(constellation) : undefined;
    galaxy.setCurrentStation(t ? stationDay : null);
    // 漫游时也收着：当前站的环跟着走（上面 setCurrentStation 照常记），字幕条漫游完再出来
    const visible = Boolean(t) && selected < 0 && !galaxy.inCarousel && !galaxy.roaming;
    appRoot.classList.toggle('constel-on', visible);
    if (!t || !visible) return constelBar.hide();
    const s = stations(t.id), at = stationIndex(s, stationDay);
    constelBar.show({ name: t.name, index: at, total: s.length, dateText: at && stationDay ? dotted(stationDay) : '' });
  };
  /** 面板开着时，主题行跟着变（哪个是正在看的星座、打了哪些） */
  const refreshPanelText = () => {
    const i = galaxy.selectedIndex;
    if (i >= 0 && panel.open) panel.text(describe(i));
  };
  const CONSTEL_HINT_KEY = 'star-sea/constellation-hint';
  const enterConstellation = (themeId: string) => {
    const t = themes.get(themeId);
    if (!t) return;
    const s = stations(themeId);
    if (!s.length) return void toast(shell.toasts, `「${t.name}」还没有一天，先在照片下面给那一天打上它`);
    // 从面板里进来：先关上面板，镜头要去看整个星座
    exitRing(false);
    if (galaxy.selectedIndex >= 0) galaxy.select(-1, false);
    constellation = themeId;
    stationDay = null;
    galaxy.enterConstellation(s, t.name);
    refreshStarmap();
    refreshConstelBar();
    // 每台设备第一次进星座时说一声怎么出来
    try {
      if (!localStorage.getItem(CONSTEL_HINT_KEY)) {
        localStorage.setItem(CONSTEL_HINT_KEY, '1');
        toast(shell.toasts, `进入「${t.name}」星座 · Esc 退出`, { ms: 6000 });
      }
    } catch {
      // 存不了：不提示也无妨
    }
  };
  const exitConstellation = () => {
    if (!constellation) return;
    constellation = null;
    stationDay = null;
    galaxy.exitConstellation();
    refreshStarmap();
    refreshConstelBar();
    refreshPanelText();
  };
  /** 上一站 / 下一站：镜头飞到那一天、打开它的第一张；到头停住 */
  const stepConstellation = (dir: 1 | -1) => {
    if (!constellation) return;
    const i = galaxy.selectedIndex, from = i >= 0 ? galaxy.photos[i]!.date : stationDay;
    const day = stepStation(stations(constellation), from, dir);
    const j = day ? galaxy.firstOnDay(day) : -1;
    if (j < 0) return;
    stationDay = day;
    goTo(j);
  };
  /**
   * 时光漫游（design §11.11）的候选：每个还有照片的日子一项，挑站打分要用的信息都在里面（roam.ts）。
   * 压测模式（fake）也有回忆和照片，照样能漫游。
   */
  const roamDays = (): RoamDay[] => {
    const md = new Set(birthdays().map((b) => b.monthDay));
    const days = new Set<LocalDate>();
    for (let i = 0; i < galaxy.photos.length; i++) if (!galaxy.isGone(i)) days.add(galaxy.photos[i]!.date);
    const out: RoamDay[] = [];
    for (const date of [...days].sort()) {
      // 一天一段回忆：取这天第一张（没删的）照片所属的那段（同 dayInfo）；createdBy、共鸣看这天的所有照片
      const idx = galaxy.sameDay(galaxy.firstOnDay(date));
      const ps = idx.map((i) => galaxy.photos[i]!);
      const m = memories.get(ps[0]!.memoryId);
      out.push({
        date,
        count: ps.length,
        shine: m?.shine ?? 3,
        titled: !!m?.title?.trim(),
        story: !!m?.story?.trim(),
        themed: (memoryThemes.get(ps[0]!.memoryId)?.size ?? 0) > 0,
        both: new Set(ps.map((p) => p.createdBy)).size >= 2,
        // 双星共鸣：两个人都在这张照片上写了视角（同 markResonance）
        resonance: ps.some((p) => new Set((perspectives.get(p.id) ?? []).map((x) => x.memberId)).size >= 2),
        // 纪念日（月日和在一起那天一样）或者谁的生日；space 会变，每次按现在的算
        anniversary: date.slice(5) === space.since.slice(5) || md.has(date.slice(5)),
      });
    }
    return out;
  };

  // ── 漫游的界面（roamui.ts）：卡片、控制条、手势；这里管状态字、预取、收尾 ──
  const roamUi = mountRoamUi(shell.overlay, {
    onToggle: () => toggleRoamPause(),
    onPrev: () => seekRoam(-1),
    onNext: () => seekRoam(1),
    onExit: () => galaxy.stopRoam(),
    onFast: (on) => setRoamFast(on),
  });
  /** 这一趟的站（onArrive 的卡片、控制条的日期、预取都要用）；没漫游时内容不作数 */
  let roamStops: LocalDate[] = [];
  /** 这一趟漫游是哪个星座的（在星座里开始的漫游）；null 是整片星海 */
  let roamTheme: { id: string; name: string } | null = null;
  /** 取到的 512 档：photoId → 对象 URL（这一趟内有效，漫游停了才回收）；inflight 是还没回来的请求 */
  const roamFetched = { urls: new Map<string, string>(), inflight: new Map<string, AbortController>() };
  /** 长按快进中：控制条要显示「快进中 ×2.5」 */
  let roamFast = false;
  /** 走完时的「再走一遍」提示：按 T、点菜单重新开始时收掉，免得盖在新一趟的卡片上 */
  let roamEndToast: ToastHandle | null = null;
  /** 有没有两天以上还有照片（「星图」菜单的漫游一项能不能点）：照片一多每次都挑一遍站太浪费，数到第二天就停 */
  const roamable = () => {
    let first: string | null = null;
    for (let i = 0; i < galaxy.photos.length; i++) {
      if (galaxy.isGone(i)) continue;
      const d = galaxy.photos[i]!.date;
      if (first === null) first = d;
      else if (d !== first) return true;
    }
    return false;
  };

  /** 这一站显示哪张照片：回忆的封面（还在的话），否则这天第一张没删的；一天都没有是 -1 */
  const roamCover = (date: LocalDate): number => {
    const first = galaxy.firstOnDay(date);
    if (first < 0) return -1;
    const memory = memories.get(galaxy.photos[first]!.memoryId);
    const day = galaxy.sameDay(first);
    if (memory?.coverPhotoId) {
      const i = galaxy.indexOf(memory.coverPhotoId);
      if (i >= 0 && day.includes(i)) return i;
    }
    return first;
  };

  /** 到站时卡片上显示的内容（roamui.ts）：文字同面板的 describe，照片是这天的封面 */
  const roamSlide = (stop: number): RoamSlide | null => {
    const date = roamStops[stop];
    if (!date) return null;
    const i = roamCover(date);
    const p = i >= 0 ? galaxy.photos[i]! : null;
    const memory = p ? memories.get(p.memoryId) : undefined;
    const day = i >= 0 ? galaxy.sameDay(i) : [];
    const d = new Date(`${date}T12:00:00`);
    const nth = daysBetween(space.since, date) + 1;
    // 这一天是谁点亮的：两个人都添过就是「你们一起」；漫游中照片被删光（who 空了）就都不写
    const who = new Set(day.map((k) => galaxy.photos[k]!.createdBy));
    const by = who.size >= 2 ? '你们一起点亮' : who.size === 1 ? `由 ${nameOf([...who][0]!)} 点亮` : '';
    const place = memory?.place?.name ?? (p && p.lat !== undefined && p.lng !== undefined ? coordinates(p.lat, p.lng) : '');
    const meta = [place, day.length ? `${day.length} 张` : '', by].filter(Boolean).join(' · ');
    return {
      aspect: Math.min(1.8, Math.max(0.56, p && p.width && p.height ? p.width / p.height : 4 / 3)),
      swatch: `radial-gradient(circle at 30% 30%, ${p?.color ?? '#8ea0ff'}, #0b0d1a)`,
      placeholder: p?.thumbhash ? thumbHashToDataURL(Uint8Array.from(atob(p.thumbhash), (c) => c.charCodeAt(0))) : undefined,
      image: p ? roamFetched.urls.get(p.id) : undefined,
      date: `${d.getFullYear()} 年 ${d.getMonth() + 1} 月 ${d.getDate()} 日 · 星期${WEEKDAY.charAt(d.getDay())}`,
      title: memory?.title ?? (nth > 0 ? `在一起的第 ${nth} 天` : '认识之前'),
      meta,
      story: memory?.story?.split('\n')[0]?.trim() ?? '',
      // 照片的推近走这一站停的时长
      hold: stopHold(memory?.shine ?? 3),
    };
  };

  /** 控制条的一行字：第几段（星座漫游叫站）、那天的日期；还没到第一站没有日期；暂停、快进加后缀 */
  const roamLine = () => {
    const s = galaxy.roamState;
    if (!s) return '';
    const date = s.stop >= 0 ? roamStops[s.stop] : undefined;
    // 星座漫游的头是「漫游「旅行」」，整片星海是「时光漫游」
    const head = roamTheme ? `漫游「${roamTheme.name}」` : '时光漫游';
    const unit = roamTheme ? '站' : '段';
    const base = s.stop < 0 ? `${head} · ${s.stops} ${unit}` : `${head} · 第 ${s.stop + 1} / ${s.stops} ${unit} · ${date ? dotted(date) : ''}`;
    return `${base}${s.paused ? ' · 已暂停' : ''}${roamFast ? ' · 快进中 ×2.5' : ''}`;
  };
  const refreshRoamLine = () => roamUi.line(roamLine());

  /** 点一下画面或卡片、空格、按钮：暂停 / 继续 */
  const toggleRoamPause = () => {
    const s = galaxy.roamState;
    if (!s) return;
    galaxy.pauseRoam(!s.paused);
    roamUi.paused(galaxy.roamState?.paused ?? false);
    refreshRoamLine();
  };

  /** 按住快进 ×2.5、松手恢复 1（手势层的长按） */
  const setRoamFast = (on: boolean) => {
    roamFast = on;
    galaxy.setRoamSpeed(on ? 2.5 : 1);
    refreshRoamLine();
  };

  /** 上一段 / 下一段；还没到第一站时（stop 是 -1）下一段去第 0 站、上一段不动 */
  const seekRoam = (dir: 1 | -1) => {
    const s = galaxy.roamState;
    if (!s || (dir < 0 && s.stop < 0)) return;
    galaxy.seekRoam(s.stop + dir);
  };

  /** 取当前站和后面两站的 512 档（同时最多 2 个请求；api.photoBlob 本来就走离线缓存）。压测、没缩略图的（HEIC）跳过 */
  const scheduleRoamFetch = () => {
    const s = galaxy.roamState;
    if (!s) return;
    const wanted: string[] = [];
    for (let k = Math.max(0, s.stop); k < roamStops.length && k < s.stop + 3; k++) {
      const i = roamCover(roamStops[k]!);
      const p = i >= 0 ? galaxy.photos[i]! : null;
      if (p && p.thumbs && !fake && !wanted.includes(p.id)) wanted.push(p.id);
    }
    // 窗口挪走了（到站、跳站）：撤掉不再要的请求
    for (const [id, c] of roamFetched.inflight)
      if (!wanted.includes(id)) {
        c.abort();
        roamFetched.inflight.delete(id);
      }
    for (const id of wanted) {
      if (roamFetched.inflight.size >= 2) break;
      if (roamFetched.urls.has(id) || roamFetched.inflight.has(id)) continue;
      const c = new AbortController();
      roamFetched.inflight.set(id, c);
      void api.photoBlob(id, 't512', c.signal).then(
        (blob) => {
          // 同一个 id 可能已被撤销又重新排上：只删自己这个请求
          if (roamFetched.inflight.get(id) === c) roamFetched.inflight.delete(id);
          // 漫游已经停了：这一张没用了
          if (!galaxy.roaming) return;
          const url = URL.createObjectURL(blob);
          roamFetched.urls.set(id, url);
          // 正在看的就是这一张：换清晰图
          const cur = galaxy.roamState;
          if (cur && cur.stop >= 0) {
            const i = roamCover(roamStops[cur.stop]!);
            if (i >= 0 && galaxy.photos[i]!.id === id) roamUi.setImage(url);
          }
          scheduleRoamFetch();
        },
        () => {
          if (roamFetched.inflight.get(id) === c) roamFetched.inflight.delete(id);
          scheduleRoamFetch();
        },
      );
    }
  };

  /** 漫游停了：没完成的请求都取消；对象 URL 等卡片淡出完再回收（revoke 后图立刻不见） */
  const stopRoamFetch = () => {
    for (const c of roamFetched.inflight.values()) c.abort();
    roamFetched.inflight.clear();
    const urls = [...roamFetched.urls.values()];
    roamFetched.urls.clear();
    setTimeout(() => {
      for (const u of urls) URL.revokeObjectURL(u);
    }, 600);
  };

  /** 到站：换这一站的卡片、状态行、预取窗口，轻轻的到站声；星座漫游里当前站的环跟着走 */
  const arriveRoam = (stop: number) => {
    const v = roamSlide(stop);
    if (v) roamUi.arrive(v);
    if (roamTheme) {
      const day = roamStops[stop];
      // stationDay 跟着漫游走：星座的站环（refreshConstelBar 里的 setCurrentStation）停在正在看的这一站上
      if (day) stationDay = day;
      refreshConstelBar();
    }
    refreshRoamLine();
    scheduleRoamFetch();
    sound.roamTick();
  };

  /** 漫游停了，不管什么原因：界面收起、顶栏回来、字幕条按原规则回来、快进和预取清掉；走完了再提示「再走一遍」 */
  const endRoam = (finished: boolean) => {
    const ended = roamTheme;
    roamTheme = null;
    roamFast = false;
    stopRoamFetch();
    roamUi.hide();
    photoFocus(false, 'roam');
    hud.setAway(false);
    galaxy.setViewShift(0);
    refreshConstelBar();
    // 「再走一遍」重新挑站：这期间 Ta 刚添的照片也算进去。星座里的漫游走完时星座还开着才回到它；
    // 中途星座被退出了（Ta 删了这个主题）按整片星海算
    if (finished) {
      const back = ended !== null && constellation === ended.id ? ended : null;
      roamEndToast = toast(shell.toasts, back ? `沿「${back.name}」走了一遍` : '这段日子又走了一遍', {
        action: { label: '再走一遍', onClick: () => startRoam(back ? back.id : null) },
        ms: 8000,
      });
    }
  };

  /** 每台设备第一次开始漫游时提示一次怎么操作；照 CONSTEL_HINT_KEY 的写法，存不了就不提示 */
  const ROAM_HINT_KEY = 'star-sea/roam-hint';
  /**
   * 开始时光漫游（「星图」菜单、T）。先收起轮转、照片面板（编辑跟着面板关掉）、胶囊面板。
   * themeId 给了是星座里的漫游（在星座里按 T、菜单的「漫游这个星座」）：星座开着不动，沿金线走它的各站；
   * 没给是整片星海（在星座里点「漫游整片星海」先退出星座，再照常漫游）。
   * 只有挑不出站时提示：整片星海「再点亮一天」、星座「至少两站才能沿线走」；
   * galaxy.startRoam 返回 false 的别的原因（入场没放完、已经在漫游、星轨还没长好）不提示。
   */
  const startRoam = (themeId: string | null = null) => {
    roamEndToast?.close();
    roamEndToast = null;
    exitRing(false);
    galaxy.select(-1, false);
    closeCapsule();
    const days = roamDays();
    const shineOf = new Map(days.map((d) => [d.date, d.shine] as const));
    let stops: LocalDate[], ct: { id: string; name: string } | null = null;
    if (themeId) {
      const t = themes.get(themeId);
      if (!t) return;
      const all = stations(themeId);
      // 不超过 16 站全走、按日期；超过时用挑站那套打分，只在这个主题自己的日子里挑 16 站
      const inside = new Set(all);
      stops = all.length <= 16 ? all : pickStops(days.filter((d) => inside.has(d.date)));
      if (stops.length < 2) return void toast(shell.toasts, `「${t.name}」至少两站才能沿线走`);
      ct = { id: t.id, name: t.name };
    } else {
      // 整片星海：在星座里点「漫游整片星海」先退出星座
      if (constellation) exitConstellation();
      stops = pickStops(days);
      if (!stops.length) return void toast(shell.toasts, '再点亮一天，就能漫游了');
    }
    const ok = galaxy.startRoam(
      stops,
      stops.map((d) => shineOf.get(d)!),
      {
        onArrive: arriveRoam,
        onDepart: () => roamUi.depart(),
        onTick: (t, total) => roamUi.progress(t / total),
        onStop: endRoam,
      },
      ct ? { constellation: true } : undefined,
    );
    if (!ok) return;
    roamStops = stops;
    roamTheme = ct;
    roamFast = false;
    hud.setAway(true);
    // 宽屏的卡片占着上半：星团往下让到卡片下面（约四分之三高处）；窄屏卡片本来就占满了，不动
    if (!narrow.matches) galaxy.setViewShift(-0.25);
    roamUi.show();
    photoFocus(true, 'roam');
    refreshRoamLine();
    refreshConstelBar();
    scheduleRoamFetch();
    try {
      if (!localStorage.getItem(ROAM_HINT_KEY)) {
        localStorage.setItem(ROAM_HINT_KEY, '1');
        // 触屏没有 Esc、按住是快进：提示换成点一下暂停、左右滑换一段
        toast(shell.toasts, matchMedia('(pointer: coarse)').matches ? '跟着彗星走 · 点一下暂停 · 左右滑换一段' : '跟着彗星走 · 点一下画面暂停 · Esc 退出', { ms: 6000 });
      }
    } catch {
      // 存不了：不提示也无妨
    }
  };
  /**
   * 主题或者打的主题变了（自己做的、推送来的），或者照片加了、删了：星图菜单的站数、正在看的星座的线和压暗、
   * 字幕条、面板的主题行、开着的打主题对话框都跟着变。changed 为 false 时只是照片变了，不用存快照
   */
  let themeView: { refresh(): void } | null = null;
  const afterThemes = (changed = true) => {
    const t = constellation ? themes.get(constellation) : undefined;
    if (t) galaxy.updateConstellation(stations(t.id), t.name);
    refreshStarmap();
    refreshConstelBar();
    refreshPanelText();
    themeView?.refresh();
    if (changed) remember();
  };
  const applyTheme = (t: ThemeDto) => {
    themes.set(t.id, t);
    afterThemes();
  };
  const dropTheme = (id: string) => {
    if (!themes.delete(id)) return;
    for (const set of memoryThemes.values()) set.delete(id);
    if (constellation === id) exitConstellation();
    afterThemes();
  };
  const applyMemoryThemes = (memoryId: string, ids: readonly string[]) => {
    if (ids.length) memoryThemes.set(memoryId, new Set(ids));
    else memoryThemes.delete(memoryId);
    afterThemes();
  };
  /** 「＋ 主题」：给面板里这一天打主题 */
  const openThemes = () => {
    const i = galaxy.selectedIndex, p = galaxy.photos[i];
    if (fake || !p) return;
    if (offline) return void toast(shell.toasts, '现在连不上星海服务，连上以后再打');
    const memoryId = p.memoryId, { m, d } = parseLocalDate(p.date);
    themeView = themeDialog({
      dateText: `${m} 月 ${d} 日`,
      suggestions: THEME_SUGGESTIONS,
      rows: () => [...themes.values()].map((t) => ({ id: t.id, name: t.name, count: stations(t.id).length, on: memoryThemes.get(memoryId)?.has(t.id) ?? false })),
      toggle: async (id, on) => {
        const res = await api.tagMemory(memoryId, id, on);
        applyMemoryThemes(res.memoryId, res.themeIds);
      },
      create: async (name) => {
        // 已经有同名的就直接打上它（比如点了建议里的「旅行」，而 Ta 刚建过）
        const same = [...themes.values()].find((t) => t.name === name);
        const theme = same ?? (await api.createTheme(name)).theme;
        if (!same) applyTheme(theme);
        const res = await api.tagMemory(memoryId, theme.id, true);
        applyMemoryThemes(res.memoryId, res.themeIds);
      },
      rename: async (id, name) => applyTheme((await api.renameTheme(id, name)).theme),
      remove: async (id) => {
        await api.deleteTheme(id);
        dropTheme(id);
      },
    });
  };
  refreshStarmap();

  // ── 入场暗号（ADR-0011，notes.ts）：给 Ta 留一句话；Ta 入场时看到以后，是留的那天的足迹 ──
  const refreshFootprints = () => galaxy.setFootprints(footprintsOf(notes.values()).map((n) => ({ id: n.id, date: n.date })));
  const applyNote = (n: NoteDto) => {
    notes.set(n.id, n);
    refreshFootprints();
    remember();
  };
  const writeNote = () => {
    if (offline) return void toast(shell.toasts, '现在连不上星海服务，连上以后再留');
    const partner = partnerOf()?.displayName ?? null;
    noteDialog({
      partnerName: partner,
      pending: myPendingNote(notes.values(), myId),
      lastRead: lastReadNote(notes.values(), myId),
      save: async (text) => {
        const res = await api.writeNote(text, localDateOf(new Date()));
        if (res.note) applyNote(res.note);
        toast(shell.toasts, `留好了，下次 ${partner ?? 'Ta'} 打开星海时就能看到`);
      },
      withdraw: async () => {
        const mine = myPendingNote(notes.values(), myId);
        await api.writeNote(null, localDateOf(new Date()));
        if (mine && notes.delete(mine.id)) remember();
        toast(shell.toasts, '那句暗号撤回了');
      },
    });
  };
  galaxy.onFootprint = (id) => {
    const n = notes.get(id);
    if (!n?.readAt) return;
    const who = (memberId: string) => (memberId === myId ? '你' : nameOf(memberId));
    const readOn = localDateOf(new Date(n.readAt));
    footprintDialog({ text: n.text, fromName: who(n.from), toName: who(n.to), left: dayText(n.date), read: readOn === n.date ? '当天' : dayText(readOn) });
  };
  refreshFootprints();

  // ── 星海设置：名字、在一起的日子 ──
  /**
   * 改了（自己保存的回应或者推送来的）：日子变了，星按新的螺线重新排。
   * 星海没有版本号，按变更序号比先后：保存的回应和推送谁先到不一定，晚到的旧版本不能盖掉新的
   */
  const applySpace = (next: SpaceDto, by: string | null, seqOf: number) => {
    if (seqOf <= spaceSeq) return;
    spaceSeq = seqOf;
    const old = space;
    const reworded = old.introText !== next.introText || old.introHold !== next.introHold;
    const reblind = (old.blindPerspectives === true) !== (next.blindPerspectives === true);
    if (old.name === next.name && old.since === next.since && !reworded && !reblind) return;
    space = next;
    hud.setTitle(next.name);
    const moved = old.since !== next.since;
    if (moved) {
      exitRing(false);
      galaxy.setSince(next.since);
      refreshCounts();
      const i = galaxy.selectedIndex;
      if (i >= 0) panel.text(describe(i));
    }
    remember();
    if (by === me?.member.id) {
      if (moved) toast(shell.toasts, `在一起的日子改成了 ${dayText(next.since)}，星海按新的日子重新排好了`);
      return;
    }
    const name = by ? nameOf(by) : 'Ta';
    if (moved && old.name !== next.name) toast(shell.toasts, `${name} 改了星海的名字和在一起的日子（${dayText(next.since)}），星海重新排了一遍`, { ms: 8000 });
    else if (moved) toast(shell.toasts, `${name} 把在一起的日子改成了 ${dayText(next.since)}，星海重新排了一遍`, { ms: 8000 });
    else if (old.name !== next.name) toast(shell.toasts, `${name} 把星海的名字改成了「${next.name}」`);
    // 那段话下次打开时才看得到，先说一声
    else if (reworded) toast(shell.toasts, `${name} 改了入场的那段话，下次打开星海时就能看到`);
    else if (reblind)
      toast(shell.toasts, next.blindPerspectives ? `${name} 打开了写前互盲：照片的两面，写下自己那一面才能看到对方的` : `${name} 关掉了写前互盲：照片两面写的字现在都看得到了`, { ms: 8000 });
  };

  const openSettings = () => {
    if (fake) return;
    if (offline) return void toast(shell.toasts, '现在连不上星海服务，连上以后再改');
    const hold = space.introHold ?? LIMITS.introHold;
    spaceDialog({
      name: space.name,
      since: space.since,
      introText: space.introText ?? '',
      introHold: hold,
      blindPerspectives: space.blindPerspectives === true,
      birthday: (me && members.get(me.member.id)?.birthday) ?? null,
      sharePresence,
      introPlaceholder: defaultWords(daysBetween(space.since, localDateOf(new Date())) + 1, galaxy.count),
      save: async (v) => {
        // 生日、「来过」开关都是自己的资料，另走 PATCH /api/me；两样都变了合成一次请求
        const self = me ? members.get(me.member.id) : undefined;
        const mePatch: MePatch = {};
        if (self && v.birthday !== (self.birthday ?? null)) mePatch.birthday = v.birthday;
        if (v.sharePresence !== sharePresence) mePatch.sharePresence = v.sharePresence;
        if (Object.keys(mePatch).length) {
          const res = await api.updateMe(mePatch);
          applyMember(res.member);
          if (mePatch.sharePresence !== undefined) applySharePresence(res.sharePresence === true);
        }
        const patch: SpacePatch = {};
        if (v.name !== space.name) patch.name = v.name;
        if (v.since !== space.since) patch.since = v.since;
        if (v.introText !== (space.introText ?? '')) patch.introText = v.introText || null;
        if (v.introHold !== hold) patch.introHold = v.introHold;
        if (v.blindPerspectives !== (space.blindPerspectives === true)) patch.blindPerspectives = v.blindPerspectives;
        if (!Object.keys(patch).length) return;
        const res = await api.updateSpace(patch);
        applySpace(res.space, me?.member.id ?? null, res.seq);
      },
      onTrash: () => openTrash(),
      onReplay: () => {
        try {
          sessionStorage.setItem(INTRO_NEXT_KEY, 'full');
        } catch {
          // 浏览器不让存：刷新以后照常放这一档
        }
        location.reload();
      },
    });
  };

  // ── 删除和回收站（服务端 trash.ts）：两个人都能删、都能恢复 ──
  /** 回收站开着时：Ta 那边删了、恢复了，重新列一遍 */
  let trashView: { refresh(): void } | null = null;
  let trashTimer: ReturnType<typeof setTimeout> | undefined;
  const refreshTrash = () => {
    if (!trashView) return;
    clearTimeout(trashTimer);
    trashTimer = setTimeout(() => trashView?.refresh(), 300);
  };

  /** 同一个人接连删、接连恢复的照片合成一条提示，10 秒内来的算一组 */
  interface Tally {
    by: string;
    ids: string[];
    note: ToastHandle | null;
    timer?: ReturnType<typeof setTimeout>;
  }
  /** 分组：'bin' 是放进回收站、'back' 是恢复、`theme:…` 是 Ta 给这个主题打上的回忆 */
  const tallies = new Map<string, Tally>();
  const tally = (kind: string, by: string, id: string, render: (ids: string[]) => { text: string; action: { label: string; onClick: () => void } }) => {
    let t = tallies.get(kind);
    if (!t || t.by !== by) tallies.set(kind, (t = { by, ids: [], note: null }));
    t.ids.push(id);
    const group = t, { text, action } = render([...t.ids]);
    // 点了提示里的按钮，这一组就结束了，再来的另起一条
    const act = {
      label: action.label,
      onClick: () => {
        if (tallies.get(kind) === group) tallies.delete(kind);
        action.onClick();
      },
    };
    if (t.note) t.note.done(text, act, 10_000);
    else t.note = toast(shell.toasts, text, { action: act, ms: 10_000 });
    clearTimeout(t.timer);
    t.timer = setTimeout(() => tallies.get(kind) === group && tallies.delete(kind), 10_000);
  };

  /** 这一天的照片多了、少了：面板里「第几张 / 共几张」和轮转按钮跟着变 */
  const refreshPanel = () => {
    const s = galaxy.selectedIndex;
    if (s >= 0 && panel.open) panel.text(describe(s));
  };

  /** 照片放进了回收站（自己删的或者推送来的）：星熄灭；面板正显示它就换到这一天的下一张，没有了就关上 */
  const applyDeleted = (id: string): boolean => {
    const i = galaxy.indexOf(id);
    if (i < 0 || galaxy.isGone(i)) return false;
    const day = galaxy.sameDay(i), shownNow = galaxy.selectedIndex === i;
    if (galaxy.inCarousel && ringDay.includes(i)) exitRing(false);
    galaxy.remove(id);
    if (shownNow) {
      const rest = day.filter((k) => k !== i);
      if (rest.length) galaxy.select(rest[Math.min(day.indexOf(i), rest.length - 1)]!);
      else galaxy.select(-1, false);
    } else refreshPanel();
    refreshCounts();
    remember();
    return true;
  };

  /** 从回收站恢复了（「撤销」「恢复」、重传，或者推送来的）：星在原来的位置重新亮起来 */
  const applyRestored = (photo: PhotoDto, memory: MemoryDto): number => {
    applyMemory(memory);
    const i = galaxy.add(photo);
    refreshPanel();
    refreshCounts();
    remember();
    return i;
  };

  /** 提示里的「撤销」：一组里有的可能已经被恢复过、或者已经永久删除了 */
  const restoreAll = async (ids: string[]) => {
    let last = -1, ok = 0, failed = 0, why = '';
    for (const id of ids) {
      try {
        const r = await api.restorePhoto(id);
        last = applyRestored(r.photo, r.memory);
        ok++;
      } catch (e) {
        failed++;
        why = e instanceof Error ? e.message : String(e);
      }
    }
    const target = last;
    const text = [ok ? (ok > 1 ? `已恢复 ${ok} 张照片` : '照片已恢复') : '', failed ? `${failed} 张没恢复成（${why}）` : ''].filter(Boolean).join('，');
    toast(shell.toasts, text, target >= 0 ? { action: { label: '去看看', onClick: () => goTo(target) } } : {});
  };

  const binNote = (by: string, id: string) =>
    tally('bin', by, id, (ids) => ({
      text: by === me?.member.id ? `${ids.length > 1 ? `${ids.length} 张照片` : '照片'}已放进回收站` : `${nameOf(by)} 把 ${ids.length} 张照片放进了回收站`,
      action: { label: '撤销', onClick: () => void restoreAll(ids) },
    }));

  const deleteShown = async () => {
    const p = galaxy.photos[galaxy.selectedIndex];
    if (fake || !p) return;
    if (offline) return void toast(shell.toasts, '现在连不上星海服务，连上以后再删');
    const ok = await confirmDialog({
      title: '删除这张照片？',
      text: `照片会放进回收站，${LIMITS.trashDays} 天内你们两个人都能在「星海设置 → 回收站」里恢复。Ta 那边这颗星也会熄灭。`,
      confirm: '删除',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deletePhoto(p.id);
      sinkBuzz();
      // 推送可能比回应先到、已经把星熄灭了，提示照样给
      applyDeleted(p.id);
      if (me) binNote(me.member.id, p.id);
    } catch (e) {
      toast(shell.toasts, `没删掉：${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const openTrash = () => {
    if (fake || trashView) return;
    if (offline) return void toast(shell.toasts, '现在连不上星海服务，连上以后再打开回收站');
    let last = -1;
    trashView = trashDialog({
      load: async () => (await api.trash()).photos,
      thumb: (id) => api.photoBlob(id, 't128'),
      restore: async (p) => {
        const r = await api.restorePhoto(p.id);
        last = applyRestored(r.photo, r.memory);
      },
      purge: async (p) => {
        await api.purgePhoto(p.id);
        void forgetThumbs(p.id);
      },
      nameOf,
      onClose: () => {
        trashView = null;
        const target = last;
        if (target >= 0) toast(shell.toasts, '恢复的照片回到星海里了', { action: { label: '去看看', onClick: () => goTo(target) } });
      },
    });
  };

  if (import.meta.env.DEV) Object.assign(window, { __starSea: { galaxy, api, sound } });
  if (fake || !me) return;

  // ── Ta 赴约的并轨（P3b，design §11.12，接力点亮的发起方那一侧）：提示照旧「已赴约」；镜头在全景当场放，
  //    不在全景（看照片、漫游、星座、轮转、伪装、飞行中）提示带「去看看」回全景，之后每 0.4 秒查一次、到全景就放 ──
  const mergePartner = (m: MemberDto) => {
    // 放并轨之前双星藏着，中心还是单光晕
    galaxy.setTwins('held', me.member.tint);
    // 并轨锁定（或轻触、Esc 落定）才记上 Ta：没放完就关了页面，下次打开还会补放
    const play = () =>
      void galaxy
        .playMerge({
          onLock: () => {
            sound.chime();
            heartbeatBuzz();
          },
        })
        .then(() => saveMembersSeen([...members.keys()]));
    const text = `${m.displayName} 已赴约，和你一起点亮了这片星海`;
    if (galaxy.atOverview()) {
      toast(shell.toasts, text, { ms: 9000 });
      return play();
    }
    // 「去看看」回到全景：atOverview 要求没有选中的星、不在漫游 / 轮转 / 星座里，所以只飞镜头不够（照片面板开着就一直等下去）；
    // 照 HUD「回全景」和开始漫游前的收法。伪装不替人退出：伪装时并轨接着等，退出伪装、回到全景再放
    const goSee = () => {
      if (galaxy.roaming) galaxy.stopRoam();
      exitRing(false);
      closeCapsule();
      exitConstellation();
      galaxy.select(-1, false);
      galaxy.frameAll(true);
    };
    toast(shell.toasts, text, { ms: 9000, action: { label: '去看看', onClick: goSee } });
    const timer = setInterval(() => {
      // 入场还没放完不算；atOverview 自己排除看照片、漫游、星座、轮转、伪装和飞行中。
      // 页面在后台不用另外等：那时不出帧、星海的时钟也停着，这时开始的并轨回到前台才真正动起来（同当场放的那条路）
      if (introPlaying || !galaxy.atOverview()) return;
      clearInterval(timer);
      play();
    }, 400);
  };
  {
    // 有并轨要放（catchup）时先只记上不含 Ta 的那份：并轨锁定才记全——没放完就关了页面，下次打开还会补放
    saveMembersSeen([...members.keys()].filter((id) => !catchup || id !== partner?.id));
    if (catchup && partner) void introDone.then(() => setTimeout(() => mergePartner(partner), 700));
  }

  // ── 「来过」（P3b，ADR-0013）：两个人都开了开关、Ta 24 小时内来过时，双星里 Ta 那颗亮一些。
  //    自己开着开关才轮询（每 10 分钟、回前台时）；失败静默，下一轮再说 ──
  let sharePresence = me.sharePresence === true;
  let presenceTimer: ReturnType<typeof setInterval> | undefined;
  /** 上次取过的时刻（进来时快照里那项也算）：回前台那一取至少隔 1 分钟 */
  let presenceAt = Date.now();
  const fetchPresence = () => {
    // 页面不在前台不取：回到前台时 presenceVisible 会补（超过 1 分钟才取）
    if (document.hidden) return;
    presenceAt = Date.now();
    api
      .presence()
      .then((r) => galaxy.setPartnerRecent(r.recent))
      .catch(() => {});
  };
  const presenceVisible = () => {
    if (document.visibilityState === 'visible' && Date.now() - presenceAt >= 60_000) fetchPresence();
  };
  const startPresence = () => {
    if (presenceTimer !== undefined) return;
    presenceTimer = setInterval(fetchPresence, 10 * 60_000);
    document.addEventListener('visibilitychange', presenceVisible);
  };
  const stopPresence = () => {
    clearInterval(presenceTimer);
    presenceTimer = undefined;
    document.removeEventListener('visibilitychange', presenceVisible);
  };
  // 星海设置里的开关变了（openSettings 的保存里调）：开就立刻取一次并开始轮询；关就停，Ta 那颗当场回到平常亮度
  const applySharePresence = (on: boolean) => {
    sharePresence = on;
    if (!on) {
      stopPresence();
      galaxy.setPartnerRecent(false);
      return;
    }
    fetchPresence();
    startPresence();
  };
  // 离线打开不取；中途连上时在推送的 open 那里开始
  if (sharePresence && !offline) startPresence();

  // ── 实时点亮 ──
  let lit: { count: number; last: number; by: string; note: ReturnType<typeof toast> | null; timer?: ReturnType<typeof setTimeout> } | null = null;
  const partnerLit = (by: string, index: number) => {
    if (!lit || lit.by !== by) lit = { count: 0, last: index, by, note: null };
    lit.count++;
    lit.last = index;
    const name = members.get(by)?.displayName ?? 'Ta';
    const text = `${name} 刚点亮了 ${lit.count} 颗星`;
    const target = lit.last;
    if (lit.note) lit.note.done(text, { label: '去看看', onClick: () => goTo(target) }, 8000);
    else lit.note = toast(shell.toasts, text, { action: { label: '去看看', onClick: () => goTo(target) }, ms: 8000 });
    clearTimeout(lit.timer);
    lit.timer = setTimeout(() => (lit = null), 8000);
  };

  const onChange = (c: Change) => {
    seq = Math.max(seq, c.seq);
    remember();
    if (c.kind === 'photo.added') {
      applyMemory(c.memory);
      const known = galaxy.indexOf(c.photo.id) >= 0;
      const i = galaxy.add(c.photo);
      if (!known) {
        refreshCounts();
        if (c.by && c.by !== me.member.id) partnerLit(c.by, i);
      }
    } else if (c.kind === 'member.updated') {
      const before = members.get(c.member.id);
      applyMember(c.member);
      // Ta 填了、改了生日：说一声（清掉了不说）
      const md = c.member.birthday;
      if (md && md !== before?.birthday && c.by && c.by !== me.member.id) toast(shell.toasts, `${c.member.displayName} 把生日记在了星海里：${Number(md.slice(0, 2))} 月 ${Number(md.slice(3, 5))} 日`);
    } else if (c.kind === 'device.recovered') {
      // 有一台设备用找回码加入了（只发给被找回的人，ADR-0016）：每台都说一声，不是自己加的就去移除。刚加入的那台自己不说
      if (c.device.id !== me.device.id)
        toast(shell.toasts, recoveredNotice(c.device, nameOf), { sticky: true, action: { label: '我的设备', onClick: () => openDevices() } });
    } else if (c.kind === 'member.joined') {
      const fresh = !members.has(c.member.id);
      members.set(c.member.id, c.member);
      galaxy.setMember(c.member);
      // 正开着星海时 Ta 加入了：排一个并轨（入场还没放完就等它放完）；members-seen 同样等并轨锁定才记上 Ta
      if (fresh && c.member.id !== me.member.id) {
        saveMembersSeen([...members.keys()].filter((id) => id !== c.member.id));
        void introDone.then(() => mergePartner(c.member));
      } else {
        saveMembersSeen([...members.keys()]);
      }
    } else if (c.kind === 'memory.updated') {
      const before = memories.get(c.memory.id);
      if (!applyMemory(c.memory) || !c.by || c.by === me.member.id) return;
      const text = editNote(nameOf(c.by), dayText(c.memory.date), before ? fieldsOf(before) : null, fieldsOf(c.memory));
      // 正在编辑这段回忆的话，表单里已经提醒过了
      if (!text || editing?.memoryId === c.memory.id) return;
      // 正看着这段回忆就不用「去看看」了
      const shownNow = galaxy.selectedIndex >= 0 && galaxy.photos[galaxy.selectedIndex]?.memoryId === c.memory.id;
      const target = galaxy.firstOf(c.memory.id);
      toast(shell.toasts, text, { action: shownNow || target < 0 ? undefined : { label: '去看看', onClick: () => goTo(target) }, ms: 8000 });
    } else if (c.kind === 'space.updated') {
      applySpace(c.space, c.by ?? null, c.seq);
    } else if (c.kind === 'photo.deleted') {
      const lit = applyDeleted(c.photo.id);
      // 自己这台设备做的改动，回应里已经处理过了；别人做的（Ta，或者自己的另一台设备）才提示、刷新回收站
      if (c.by === me.member.id) return;
      refreshTrash();
      if (lit && c.by) binNote(c.by, c.photo.id);
    } else if (c.kind === 'photo.restored') {
      const k = galaxy.indexOf(c.photo.id), wasGone = k < 0 || galaxy.isGone(k);
      const i = applyRestored(c.photo, c.memory);
      if (c.by === me.member.id) return;
      refreshTrash();
      const by = c.by;
      if (wasGone && by) tally('back', by, c.photo.id, (ids) => ({ text: `${nameOf(by)} 从回收站恢复了 ${ids.length} 张照片`, action: { label: '去看看', onClick: () => goTo(i) } }));
    } else if (c.kind === 'photo.purged') {
      // 永久删除了：没收到放进回收站的那条（离线太久）也照样熄灭；这台设备上缓存的缩略图一起删掉
      applyDeleted(c.photoId);
      void forgetThumbs(c.photoId);
      perspectives.delete(c.photoId);
      voices.delete(c.photoId);
      voiceVersions.set(c.photoId, c.seq);
      if (c.by !== me.member.id) refreshTrash();
    } else if (c.kind === 'voice.updated') {
      applyVoices(c.photoId, c.voices, c.seq);
    } else if (c.kind === 'voice.reset') {
      if (c.seq <= voiceResetSeq) return;
      const grouped = new Map<string, VoiceDto[]>();
      for (const v of c.voices) grouped.set(v.photoId, [...(grouped.get(v.photoId) ?? []), v]);
      for (const id of new Set([...voices.keys(), ...grouped.keys()])) applyVoices(id, grouped.get(id) ?? [], c.seq);
      voiceResetSeq = c.seq;
    } else if (c.kind === 'perspective.updated') {
      const before = (perspectives.get(c.photoId) ?? []).find((x) => x.memberId === c.by);
      applyPerspectives(c.photoId, c.perspectives);
      // Ta 写了（清空的不说）：正看着这张就不用「去看看」
      const after = c.perspectives.find((x) => x.memberId === c.by);
      if (!c.by || c.by === me.member.id || !after || before?.updatedAt === after.updatedAt) return;
      const i = galaxy.indexOf(c.photoId), p = galaxy.photos[i];
      if (!p || galaxy.isGone(i)) return;
      const shownNow = galaxy.selectedIndex === i;
      const where = `${dayText(p.date)}的一张照片`;
      const text = after.hidden
        ? `${nameOf(c.by)} 在 ${where}上写了几句；写下你的视角就能看到`
        : p.createdBy === c.by
          ? `${nameOf(c.by)} 给 ${where}写了几句`
          : `${nameOf(c.by)} 翻开了 ${where}，写下了 Ta 的视角`;
      toast(shell.toasts, text, { action: shownNow ? undefined : { label: '去看看', onClick: () => goTo(i) }, ms: 8000 });
    } else if (c.kind === 'capsule.updated') {
      const before = capsules.get(c.capsule.id), cap = c.capsule;
      applyCapsule(cap);
      if (!c.by || c.by === me.member.id) return;
      const who = nameOf(c.by);
      const go = {
        label: '去看看',
        onClick: () => {
          galaxy.focusCapsule(cap.id);
          showCapsule(cap.id);
        },
      };
      if (!before) toast(shell.toasts, `${who} 封存了${cap.kind === 'pact' ? '一份写给你们的契约' : '一颗时间胶囊'}，${dayText(cap.date)}解封`, { action: go, ms: 9000 });
      else if (cap.openedBy.includes(c.by) && !before.openedBy.includes(c.by))
        toast(shell.toasts, cap.createdBy === me.member.id ? `${who} 拆开了你的时间胶囊` : `${who} 已经为你开启了一颗时间胶囊`, { action: go, ms: 9000 });
      else if (cap.peekCount > before.peekCount) toast(shell.toasts, `${who} 偷看了一眼你们的契约胶囊`, { action: go, ms: 8000 });
      else if (cap.date !== before.date) toast(shell.toasts, `${who} 把一颗时间胶囊的解封日子改到了 ${dayText(cap.date)}`, { action: go, ms: 8000 });
    } else if (c.kind === 'capsule.removed') {
      const had = capsules.get(c.capsuleId);
      // 拆开过的话这台设备上缓存着里面照片的缩略图：一起清掉（转成了普通照片的，用到时再取）
      if (had?.photo) void forgetThumbs(had.photo.id);
      capsules.delete(c.capsuleId);
      refreshCocoons();
      if (capsulePanel.id === c.capsuleId) closeCapsule();
      remember();
      if (had && c.by && c.by !== me.member.id) toast(shell.toasts, `${nameOf(c.by)} 撤回了一颗时间胶囊`);
    } else if (c.kind === 'theme.added') {
      const fresh = !themes.has(c.theme.id);
      applyTheme(c.theme);
      if (fresh && c.by && c.by !== me.member.id) toast(shell.toasts, `${nameOf(c.by)} 建了新主题「${c.theme.name}」`);
    } else if (c.kind === 'theme.updated') {
      const before = themes.get(c.theme.id);
      applyTheme(c.theme);
      if (before && before.name !== c.theme.name && c.by && c.by !== me.member.id) toast(shell.toasts, `${nameOf(c.by)} 把「${before.name}」改名成了「${c.theme.name}」`);
    } else if (c.kind === 'theme.removed') {
      const before = themes.get(c.themeId), watching = constellation === c.themeId;
      dropTheme(c.themeId);
      if (before && c.by && c.by !== me.member.id) toast(shell.toasts, `${nameOf(c.by)} 删掉了主题「${before.name}」${watching ? '，星座退出了' : ''}`);
    } else if (c.kind === 'memory.themes') {
      const before = memoryThemes.get(c.memoryId) ?? new Set<string>();
      applyMemoryThemes(c.memoryId, c.themeIds);
      // Ta 给回忆打上了主题：说一声，10 秒内接连打的同一个主题合成一条（去掉主题不说，线当场重画）
      const by = c.by, m = memories.get(c.memoryId);
      if (!by || by === me.member.id || !m) return;
      for (const id of c.themeIds) {
        const t = themes.get(id);
        if (!t || before.has(id)) continue;
        tally(`theme:${id}`, by, c.memoryId, (ids) => ({
          text: ids.length > 1 ? `${nameOf(by)} 给「${t.name}」添了 ${ids.length} 段回忆` : `${nameOf(by)} 把 ${dayText(m.date)}加进了「${t.name}」`,
          action: { label: '去看看', onClick: () => enterConstellation(id) },
        }));
      }
    } else if (c.kind === 'note.updated') {
      const before = notes.get(c.note.id);
      applyNote(c.note);
      // Ta 刚留的：先不说是什么，等下次入场时揭晓
      if (!before && c.note.to === myId && c.by && c.by !== myId) toast(shell.toasts, `${nameOf(c.note.from)} 给你留了一句话，下次打开星海时揭晓`, { ms: 7000 });
    } else if (c.kind === 'note.read') {
      applyNote(c.note);
    } else if (c.kind === 'note.removed') {
      if (notes.delete(c.noteId)) {
        refreshFootprints();
        remember();
      }
    } else if (c.kind === 'perspective.reset') {
      // 写前互盲开了或关了：整个换掉
      const old = [...perspectives.keys()];
      perspectives.clear();
      for (const p of c.perspectives) perspectives.set(p.photoId, [...(perspectives.get(p.photoId) ?? []), p]);
      for (const id of new Set([...old, ...perspectives.keys()])) markResonance(id);
      const i = galaxy.selectedIndex;
      if (i >= 0) panel.text(describe(i));
      remember();
    }
  };
  api.subscribe(
    snap.seq,
    (c) => {
      onChange(c);
      // 照片加了、删了、恢复了：主题星座的站可能跟着变
      if (c.kind.startsWith('photo.')) afterThemes(false);
    },
    (state) => {
      if (state === 'unauthorized') {
        void forget().then(() => location.reload());
        return;
      }
      if (state === 'retrying' && offline) return;
      hud.setConnection(state);
      if (state !== 'open') return;
      // 接上了：之前断网没取到的缩略图再要一次；从离线缓存打开的，缺的变更由实时推送补上
      galaxy.pool.retryFailed();
      if (offline) {
        offline = false;
        // 离线打开时没取的「来过」：连上了取一次并开始轮询
        if (sharePresence) {
          fetchPresence();
          startPresence();
        }
        offlineNote?.done('已经重新连上星海服务，星海会自动跟上最新的', { label: '刷新', onClick: () => location.reload() }, 10000);
      }
    },
  );

  // ── 添照片 ──
  let busy = Promise.resolve();
  const upload = (files: File[]) =>
    (busy = busy.then(async () => {
      const note = toast(shell.toasts, `正在点亮 1 / ${files.length}…`, { sticky: true });
      let added = 0, dup = 0, back = 0, failed = 0, heicPending = 0, lastIndex = -1, lastError = '';
      for (const [k, file] of files.entries()) {
        note.update(`正在点亮 ${k + 1} / ${files.length}…`);
        try {
          const res = await api.upload(await uploadForm(file), (f) => note.progress((k + f) / files.length));
          if (res.restored) {
            lastIndex = applyRestored(res.photo, res.memory);
            back++;
          } else {
            applyMemory(res.memory);
            lastIndex = galaxy.add(res.photo);
            if (res.duplicate) dup++;
            else added++;
          }
          if (!res.photo.thumbs && isHeic(file)) heicPending++;
        } catch (e) {
          failed++;
          lastError = e instanceof Error ? e.message : String(e);
        }
        note.progress((k + 1) / files.length);
      }
      refreshCounts();
      const parts = [
        added ? `点亮了 ${added} 颗星` : '',
        back ? `${back} 张从回收站恢复了` : '',
        dup ? `${dup} 张已经在星海里了` : '',
        failed ? `${failed} 张没传上去（${lastError}）` : '',
        heicPending ? `${heicPending} 张 HEIC 暂时没有缩略图` : '',
      ].filter(Boolean);
      const target = lastIndex;
      note.done(parts.join('，') || '没有照片', target >= 0 ? { label: '去看看', onClick: () => goTo(target) } : undefined, failed ? 12000 : 7000);
    }));
}

void boot();

// PWA：只在生产构建里注册（开发时每次改代码都要更新缓存，碍事）；注册失败不影响使用
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  addEventListener('load', () => void navigator.serviceWorker.register('/sw.js').catch(() => undefined));
}
